import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, X, Download, RefreshCw, Users, Copy, Bell, AlertCircle, AlertTriangle, Trash2, ChevronDown, ChevronUp, FileText, Megaphone, Clock, MapPin, Calendar, CheckCircle2, Loader2, MessageSquare, Printer, GripVertical } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { useCollapsedSeparators } from '@/utils/useCollapsedSeparators';
import { PartialReminderBanner } from '@/components/PartialReminderBanner';
import usePolling from '@/hooks/usePolling';
import pollGet from '@/utils/pollGet';
import LastUpdated from '@/components/LastUpdated';
import { normalizeWeightInput, parseWeight } from '@/utils/weights';
import { AttendanceGradesSection } from '@/components/AttendanceGradesSection';
import { CellNoteDialog, NoteDot } from '@/components/CellNoteDialog';
const CivicParticipationGrid = React.lazy(() => import('@/components/CivicParticipationGrid'));
const EvalComplementariaGrid = React.lazy(() => import('@/components/EvalComplementariaGrid'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Componente de celda de asistencia memoizado para evitar re-renders
const AttendanceCell = memo(({ studentId, colId, value, isReadOnly, isJO, onChange, hasNote, noteText, onOpenNote }) => {
  const [localValue, setLocalValue] = useState(value);
  const [focused, setFocused] = useState(false);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (!focused) setLocalValue(value);
  }, [value, focused]);

  useEffect(() => {
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, []);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value.toUpperCase();
    if (newValue === '' || ['1', '0', 'R', 'J'].includes(newValue)) {
      setLocalValue(newValue);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (!isReadOnly && newValue !== value) {
        saveTimeoutRef.current = setTimeout(() => onChange(studentId, colId, newValue), 500);
      }
    }
  }, [studentId, colId, value, isReadOnly, onChange]);

  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => {
    setFocused(false);
    if (saveTimeoutRef.current) { clearTimeout(saveTimeoutRef.current); saveTimeoutRef.current = null; }
    if (localValue !== value && !isReadOnly) onChange(studentId, colId, localValue);
  }, [localValue, value, studentId, colId, isReadOnly, onChange]);

  const getCellColor = (val) => {
    if (val === '1' || val === 'J') return 'bg-green-100 text-green-700';
    if (val === '0') return 'bg-red-100 text-red-700';
    if (val === 'R') return 'bg-yellow-100 text-yellow-700';
    if (val === 'JO') return 'bg-blue-100 text-blue-700';
    return 'bg-white';
  };

  if (isJO) {
    return (
      <div 
        className="w-10 h-10 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg bg-blue-100 text-blue-700 cursor-not-allowed"
        title="Justificación del Orientador - No editable"
        data-testid={`attendance-${studentId}-${colId}`}
      >
        JO
      </div>
    );
  }

  if (value && !focused) {
    return (
      <button
        type="button"
        onClick={() => onOpenNote && onOpenNote(studentId, colId)}
        className={`task-print-cell relative w-10 h-10 mx-auto text-center text-base font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${getCellColor(value)}`}
        data-testid={`attendance-${studentId}-${colId}`}
        title={hasNote && noteText ? `Justificación: ${noteText}` : 'Clic para editar o agregar nota'}
      >
        {value}
        {hasNote && <NoteDot testId={`attendance-note-dot-${studentId}-${colId}`} />}
      </button>
    );
  }

  return (
    <input
      type="text"
      maxLength={1}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={isReadOnly}
      className={`w-10 h-10 text-center text-base font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${getCellColor(localValue)} ${isReadOnly ? 'cursor-not-allowed' : ''}`}
      data-testid={`attendance-${studentId}-${colId}`}
    />
  );
});

AttendanceCell.displayName = 'AttendanceCell';

// Componente de celda de tarea memoizado (porcentaje 0-100)
const TaskCell = memo(({ studentId, colId, value, isReadOnly, onChange, hasNote, noteText, onOpenNote }) => {
  const [localValue, setLocalValue] = useState(value);
  const [focused, setFocused] = useState(false);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (!focused) setLocalValue(value);
  }, [value, focused]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const scheduleSync = useCallback((val) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      onChange(studentId, colId, val);
    }, 500);
  }, [studentId, colId, onChange]);

  const handleChange = useCallback((e) => {
    const raw = e.target.value;
    if (raw === '') {
      setLocalValue('');
      if (!isReadOnly) scheduleSync('');
      return;
    }
    if (/^\d{1,3}$/.test(raw)) {
      const num = parseInt(raw, 10);
      if (num <= 100) {
        setLocalValue(raw);
        if (!isReadOnly) scheduleSync(raw);
      }
    }
  }, [isReadOnly, scheduleSync]);

  const handleFocus = useCallback(() => setFocused(true), []);

  const handleBlur = useCallback(() => {
    setFocused(false);
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (localValue !== value && !isReadOnly) {
      onChange(studentId, colId, localValue);
    }
  }, [localValue, value, studentId, colId, isReadOnly, onChange]);

  const getCellColor = (val) => {
    if (val === '' || val === undefined || val === null) return 'bg-white';
    const num = parseInt(val, 10);
    if (isNaN(num)) return 'bg-white';
    if (num >= 80) return 'bg-green-100 text-green-700';
    if (num >= 60) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const isFilled = value !== '' && value !== undefined && value !== null;

  // Filled cell (not actively typing): show as a button that opens the note/edit
  // popover. Empty cells stay as a fast-entry input (number only) as before.
  if (isFilled && !focused) {
    return (
      <button
        type="button"
        onClick={() => onOpenNote && onOpenNote(studentId, colId)}
        className={`task-print-cell relative w-16 h-10 text-center text-sm font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${getCellColor(value)}`}
        data-testid={`task-${studentId}-${colId}`}
        title={hasNote && noteText ? `Justificación: ${noteText}` : "Clic para editar nota o calificación"}
      >
        {`${value}%`}
        {hasNote && (
          <span
            className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-indigo-500 border border-white"
            data-testid={`task-note-dot-${studentId}-${colId}`}
            title="Tiene justificación"
          />
        )}
      </button>
    );
  }

  const display = focused ? localValue : (localValue !== '' && localValue !== undefined && localValue !== null ? `${localValue}%` : '');

  return (
    <input
      type="text"
      maxLength={3}
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={isReadOnly}
      className={`w-16 h-10 text-center text-sm font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${getCellColor(localValue)} ${isReadOnly ? 'cursor-not-allowed' : ''}`}
      data-testid={`task-${studentId}-${colId}`}
    />
  );
});

TaskCell.displayName = 'TaskCell';

// Isolated note/edit popover with its own local state. Typing here does NOT
// re-render the (large) ClassView/grid, which keeps input fast.
const TaskNoteDialog = ({ open, studentId, colId, initialValue, initialText, isReadOnly, onClose, onSave }) => {
  const [value, setValue] = useState(initialValue);
  const [text, setText] = useState(initialText);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setText(initialText);
    }
  }, [open, studentId, colId, initialValue, initialText]);

  const handleSave = async () => {
    const cleaned = String(value).replace('%', '').trim();
    if (cleaned !== '') {
      const n = parseInt(cleaned, 10);
      if (isNaN(n) || n < 0 || n > 100) {
        toast.error('La calificación debe ser un número entre 0 y 100');
        return;
      }
    }
    setSaving(true);
    try {
      await onSave(studentId, colId, cleaned, String(text || '').slice(0, 280));
    } catch (e) {
      console.error('Error saving note:', e);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="task-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-600" />
            Calificación y justificación
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm">Calificación (%)</Label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={3}
              value={value}
              onChange={(e) => {
                const raw = e.target.value.replace('%', '');
                if (raw === '' || (/^\d{1,3}$/.test(raw) && parseInt(raw, 10) <= 100)) {
                  setValue(raw);
                }
              }}
              disabled={isReadOnly}
              className="mt-1 w-24 h-9 px-3 text-center font-bold border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400"
              data-testid="task-note-value-input"
            />
          </div>
          <div>
            <Label className="text-sm">Justificación / nota (máx. 280)</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 280))}
              disabled={isReadOnly}
              rows={4}
              maxLength={280}
              placeholder={isReadOnly ? 'Sin justificación' : 'Ej. Entregó incompleto, sin portada, etc.'}
              className="mt-1 resize-none"
              data-testid="task-note-text-input"
            />
            <p className="text-xs text-slate-400 mt-1 text-right">{(text || '').length}/280</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} data-testid="task-note-cancel-btn">
            {isReadOnly ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!isReadOnly && (
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="task-note-save-btn">
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Calcula las columnas con columnas virtuales de % Cumplimiento por parcial
const buildTaskColumnsWithCumulative = (columns) => {
  const result = [];
  let parcialIndex = 0;
  let blockTasks = [];

  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    if (col.isSeparator) {
      parcialIndex++;
      // Insertar columna de % antes del separador
      result.push({ _isCumulative: true, _parcialIndex: parcialIndex, _taskIds: blockTasks.map(t => t.id), label: `% P${parcialIndex}` });
      result.push(col);
      blockTasks = [];
    } else {
      result.push(col);
      blockTasks.push(col);
    }
  }
  // Columna final para las tareas después del último separador (o todas si no hay separadores)
  if (blockTasks.length > 0) {
    parcialIndex++;
    const label = columns.some(c => c.isSeparator) ? `% P${parcialIndex}` : '% Total';
    result.push({ _isCumulative: true, _parcialIndex: parcialIndex, _taskIds: blockTasks.map(t => t.id), label });
  }
  return result;
};

// Calcula el promedio de cumplimiento de un estudiante para un conjunto de tareas
const calcCumulative = (studentRecords, taskIds) => {
  const values = taskIds.map(id => studentRecords[id]).filter(v => v !== undefined && v !== null && v !== '');
  if (values.length === 0) return null;
  const sum = values.reduce((acc, v) => acc + (parseInt(v, 10) || 0), 0);
  return Math.round(sum / taskIds.length);
};

// Calcula las columnas de asistencia con columnas virtuales de % Asistencia por parcial
const buildAttColumnsWithCumulative = (columns) => {
  const result = [];
  let parcialIndex = 0;
  let blockCols = [];

  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    if (col.isSeparator) {
      parcialIndex++;
      result.push({ _isAttCumulative: true, _parcialIndex: parcialIndex, _colIds: blockCols.map(c => c.id || c.date), label: `% P${parcialIndex}` });
      result.push(col);
      blockCols = [];
    } else if (col.isSuspension) {
      // Suspension columns are purely informative: render them but NEVER count
      // them towards the attendance percentage.
      result.push(col);
    } else {
      result.push(col);
      blockCols.push(col);
    }
  }
  if (blockCols.length > 0) {
    parcialIndex++;
    const label = columns.some(c => c.isSeparator) ? `% P${parcialIndex}` : '% Total';
    result.push({ _isAttCumulative: true, _parcialIndex: parcialIndex, _colIds: blockCols.map(c => c.id || c.date), label });
  }
  return result;
};

// Calcula el % de asistencia de un estudiante para un conjunto de columnas
const calcAttCumulative = (studentRecord, colIds, hasJOFn, studentId, columns) => {
  let registered = 0;
  let absences = 0;
  colIds.forEach(colId => {
    const col = columns.find(c => (c.id || c.date) === colId);
    const isJO = col && hasJOFn ? hasJOFn(studentId, col) : false;
    if (isJO) {
      registered++;
    } else {
      const val = (studentRecord[colId] || '').toUpperCase();
      if (val !== '') {
        registered++;
        if (val === '0') absences++;
      }
    }
  });
  if (registered === 0) return null;
  return Math.round(((registered - absences) / registered) * 100);
};

// Igual que calcAttCumulative pero devuelve los CONTEOS (asistencias / registradas) + %.
// present = registradas - faltas (0). JO cuenta como registrada y NO como falta.
const calcAttCounts = (studentRecord, colIds, hasJOFn, studentId, columns) => {
  let registered = 0;
  let absences = 0;
  colIds.forEach(colId => {
    const col = columns.find(c => (c.id || c.date) === colId);
    const isJO = col && hasJOFn ? hasJOFn(studentId, col) : false;
    if (isJO) {
      registered++;
    } else {
      const val = (studentRecord[colId] || '').toUpperCase();
      if (val !== '') {
        registered++;
        if (val === '0') absences++;
      }
    }
  });
  const present = registered - absences;
  const pct = registered === 0 ? null : Math.round((present / registered) * 100);
  return { registered, present, pct };
};

// Re-etiqueta todos los separadores de asistencias según su orden posicional
const relabelAttSeparators = (columns) => {
  const names = ['FIN DE PRIMER PARCIAL', 'FIN DEL SEGUNDO PARCIAL', 'FIN DEL TERCER PARCIAL'];
  let sepIdx = 0;
  return columns.map(col => {
    if (col.isSeparator) {
      const newLabel = names[sepIdx] || col.label;
      sepIdx++;
      return { ...col, label: newLabel };
    }
    return col;
  });
};

// Obtiene nombre automático del separador según posición
const getAutoSeparatorLabel = (columns, position) => {
  // Contar separadores que están ANTES de esta posición
  const separatorsBefore = columns.filter((c, idx) => c.isSeparator && idx < position).length;
  const names = ['FIN DE PRIMER PARCIAL', 'FIN DEL SEGUNDO PARCIAL', 'FIN DEL TERCER PARCIAL'];
  return names[separatorsBefore] || `FIN DEL PARCIAL ${separatorsBefore + 1}`;
};

// Re-etiqueta todos los separadores de tareas según su orden posicional
const relabelTaskSeparators = (columns) => {
  const names = ['FIN DE PRIMER PARCIAL', 'FIN DEL SEGUNDO PARCIAL', 'FIN DEL TERCER PARCIAL'];
  let sepIdx = 0;
  return columns.map(col => {
    if (col.isSeparator) {
      const newLabel = names[sepIdx] || col.label;
      sepIdx++;
      return { ...col, label: newLabel };
    }
    return col;
  });
};

const ClassView = ({ user, onLogout }) => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendanceGrid, setAttendanceGrid] = useState({ columns: [], records: {} });
  const [taskGrid, setTaskGrid] = useState({ columns: [], records: {} });
  // Task notes: { studentId: { colId: text } }.
  const [taskNotes, setTaskNotes] = useState({});
  const [noteDialog, setNoteDialog] = useState(null); // { studentId, colId, initialValue, initialText } | null
  // Refs mirror the latest grid/notes so the cell callbacks stay STABLE. This keeps
  // the memoized TaskCell from re-rendering the whole grid on every keystroke/save.
  const taskGridRef = useRef({ columns: [], records: {} });
  const taskNotesRef = useRef({});
  // Mirror the attendance grid too so handleAttendanceChange can stay stable
  // (useCallback). Otherwise a new onChange breaks the memoized AttendanceCell
  // and the WHOLE attendance grid re-renders on every keystroke/commit.
  const attendanceGridRef = useRef({ columns: [], records: {} });
  useEffect(() => { taskGridRef.current = taskGrid; }, [taskGrid]);
  useEffect(() => { taskNotesRef.current = taskNotes; }, [taskNotes]);
  useEffect(() => { attendanceGridRef.current = attendanceGrid; }, [attendanceGrid]);
  // Timestamp of the teacher's last grid edit. Used to pause the roster poll
  // while the professor is actively typing so a background refresh never
  // competes with input and makes the app feel laggy.
  const lastEditRef = useRef(0);
  const markEditActivity = useCallback(() => { lastEditRef.current = Date.now(); }, []);

  // Collapsible partial-separators (per-class, localStorage-persisted)
  const attSepState = useCollapsedSeparators(`collapsed-att-${classId}`, attendanceGrid.columns);
  const taskSepState = useCollapsedSeparators(`collapsed-task-${classId}`, taskGrid.columns);
  const [taskWeights, setTaskWeights] = useState({ parcial_1: '', parcial_2: '', parcial_3: '' });
  const [attWeights, setAttWeights] = useState({ parcial_1: '', parcial_2: '', parcial_3: '' });
  useEffect(() => { if (attendanceGrid.att_weights) setAttWeights(attendanceGrid.att_weights); }, [attendanceGrid.att_weights]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('students');
  const [loadError, setLoadError] = useState(false);
  const [partialDates, setPartialDates] = useState(null);
  // Visible save indicator for grid captures: 'idle' | 'saving' | 'saved' | 'error' | 'unsaved'
  const [saveStatus, setSaveStatus] = useState('idle');
  const saveStatusTimeoutRef = useRef(null);
  // Posicion arrastrable del indicador de guardado. null = posicion por defecto
  // (abajo-derecha). El profesor puede moverlo si le estorba para capturar.
  const [indicatorPos, setIndicatorPos] = useState(() => {
    try { const s = localStorage.getItem('save_indicator_pos'); return s ? JSON.parse(s) : null; } catch (e) { return null; }
  });
  const indicatorDragRef = useRef(null);
  const handleIndicatorPointerDown = (e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    indicatorDragRef.current = {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      w: rect.width,
      h: rect.height,
      lastPos: null,
    };
    try { el.setPointerCapture?.(e.pointerId); } catch (err) { /* ignore */ }
    const onMove = (ev) => {
      const d = indicatorDragRef.current;
      if (!d) return;
      const m = 4;
      let x = ev.clientX - d.offsetX;
      let y = ev.clientY - d.offsetY;
      x = Math.max(m, Math.min(x, window.innerWidth - d.w - m));
      y = Math.max(m, Math.min(y, window.innerHeight - d.h - m));
      d.lastPos = { x, y };
      setIndicatorPos({ x, y });
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      const d = indicatorDragRef.current;
      if (d && d.lastPos) { try { localStorage.setItem('save_indicator_pos', JSON.stringify(d.lastPos)); } catch (err) { /* ignore */ } }
      indicatorDragRef.current = null;
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };
  // Pending (unsaved) cell edits, buffered and flushed by batch on section change
  // / safety-net timer / leaving the screen. Keeps data usage minimal AND avoids
  // data loss (a warning is shown while there are unsaved changes).
  const pendingAttRef = useRef({}); // { studentId: { colId: value } }
  const pendingTaskRef = useRef({});
  const flushingRef = useRef(false);
  // Flush hooks exposed by the Participación / Evaluación child grids so we can
  // flush their buffered edits on section change too (they also self-flush on unmount).
  const civicFlushRef = useRef(null);
  const evalFlushRef = useRef(null);
  // Timestamp of the last successful data refresh (shown as "Actualizado hace X").
  const [lastUpdated, setLastUpdated] = useState(null);
  const [addStudentDialog, setAddStudentDialog] = useState(false);
  const [studentName, setStudentName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [addDateDialog, setAddDateDialog] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [addTaskDialog, setAddTaskDialog] = useState(false);
  const [newTask, setNewTask] = useState({ date: '', description: '' });
  const [suspensionDialog, setSuspensionDialog] = useState(false);
  const [suspensionForm, setSuspensionForm] = useState({ date: '', motivo: '' });
  const [conflictTask, setConflictTask] = useState(null);
  const [conflictReprogram, setConflictReprogram] = useState(false);
  const [conflictNewDate, setConflictNewDate] = useState('');
  const [civicConflictCheck, setCivicConflictCheck] = useState(0);
  const [taskNamePopup, setTaskNamePopup] = useState(null);
  const [editTaskDialog, setEditTaskDialog] = useState(false);
  const [editingTaskCol, setEditingTaskCol] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({ date: '', description: '' });
  const [importGroupDialog, setImportGroupDialog] = useState(false);
  const [copyStudentsDialog, setCopyStudentsDialog] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [editStudentDialog, setEditStudentDialog] = useState(false);
  const [studentToEdit, setStudentToEdit] = useState(null);
  const [editStudentName, setEditStudentName] = useState('');
  const [orientadorJustifications, setOrientadorJustifications] = useState([]);
  
  // Partial separator state
  const [addSeparatorDialog, setAddSeparatorDialog] = useState(false);
  const [separatorType, setSeparatorType] = useState('attendance'); // 'attendance' or 'task'
  const [separatorLabel, setSeparatorLabel] = useState('');
  
  // Notification state for pending justifications
  const [pendingJustifications, setPendingJustifications] = useState([]);
  const [showJustificationNotification, setShowJustificationNotification] = useState(false);

  // Teacher notification state
  const [selectedStudentsForNotif, setSelectedStudentsForNotif] = useState([]);
  const [teacherNotifType, setTeacherNotifType] = useState(null);
  const [teacherCitatorioForm, setTeacherCitatorioForm] = useState({ fecha: '', hora: '', lugar: '', motivo: '' });
  const [teacherAvisoForm, setTeacherAvisoForm] = useState({ mensaje: '' });
  const [sendingTeacherNotif, setSendingTeacherNotif] = useState(false);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [showSentNotifications, setShowSentNotifications] = useState(false);

  useEffect(() => {
    fetchAll();
    fetchAvailableGroups();
    fetchAllClasses();
    fetchOrientadorJustifications();
  }, [classId]);

  // Fetch pending justification notifications for teacher
  useEffect(() => {
    if (user.role === 'teacher') {
      fetchPendingJustifications();
    }
  }, [classId, user.role]);

  // Fetch institution's partial load dates (for the reminder banners)
  useEffect(() => {
    if (user.role !== 'teacher') return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/teacher/partial-dates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setPartialDates(res.data))
      .catch(() => {});
  }, [user.role]);

  // Teacher view: keep the student list (names / matrículas edited by Control
  // Escolar) in sync WITHOUT a manual reload. Refetch when the window regains
  // focus / becomes visible again. (Tutor/parent view already auto-polls.)
  useEffect(() => {
    if (user.role !== 'teacher') return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchStudents(true);
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, user.role]);

  const fetchAll = async () => {
    setLoadError(false);
    await Promise.all([
      fetchClassData(),
      fetchStudents(),
      fetchAttendanceGrid(),
      fetchTaskGrid()
    ]);
    setLastUpdated(Date.now());
    setLoading(false);
  };

  // ---- Real-time auto-refresh for the read-only tutor (parent) view ----
  // Silent: swallows transient errors so a network blip never trips the
  // blocking load-error screen, and never disturbs the teacher's editing
  // (this only runs when the viewer is a tutor / read-only).
  const [civicEvalPollTick, setCivicEvalPollTick] = useState(0);
  const pollRefresh = useCallback(async () => {
    if (user.role !== 'tutor') return;
    const token = localStorage.getItem('token');
    const h = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [cls, studs, att, task, oj] = await Promise.all([
        axios.get(`${API}/classes/${classId}`, h),
        axios.get(`${API}/classes/${classId}/students`, h),
        pollGet(`${API}/attendance-grid/${classId}`, { key: `att-${classId}`, conditional: true, headers: h.headers }),
        pollGet(`${API}/task-grid/${classId}`, { key: `task-${classId}`, conditional: true, headers: h.headers }),
        axios.get(`${API}/classes/${classId}/orientador-justifications`, h),
      ]);
      setClassData(cls.data);
      setStudents([...studs.data].sort((a, b) => a.name.localeCompare(b.name)));
      if (!att.notModified) setAttendanceGrid(att.data);
      if (!task.notModified) {
        setTaskGrid(task.data);
        setTaskNotes(task.data.notes || {});
        if (task.data.task_weights) setTaskWeights(task.data.task_weights);
      }
      setOrientadorJustifications(oj.data);
      setLastUpdated(Date.now());
      setCivicEvalPollTick((t) => t + 1);
    } catch (e) {
      // Ignore transient poll errors on purpose.
    }
  }, [classId, user.role]);

  usePolling(pollRefresh, { intervalMs: 12000, enabled: user.role === 'tutor' });

  // Teacher view: auto-refresh ONLY the student roster (names / matrículas that
  // Control Escolar edits) on a timer, hands-free, WITHOUT touching the grids so
  // in-progress edits are never lost. This is what makes CE changes appear in the
  // professor's sections without reloading or switching tabs.
  const pollTeacherRoster = useCallback(async () => {
    if (user.role !== 'teacher') return;
    // Pause while the professor is actively typing: if a grid input/textarea is
    // focused, or an edit happened in the last 5s, skip this cycle entirely so
    // the background refresh never competes with input and causes lag.
    const ae = document.activeElement;
    const isTyping = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
    if (isTyping || Date.now() - lastEditRef.current < 5000) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/classes/${classId}/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sorted = [...res.data].sort((a, b) => a.name.localeCompare(b.name));
      setLastUpdated(Date.now());
      // Only update state (and trigger a re-render) if the roster actually
      // changed. Avoids the whole grid re-rendering every 8s for nothing.
      setStudents(prev => {
        const sig = (arr) => arr.map(s => `${s.id}|${s.name}|${s.matricula || ''}`).join('~');
        return sig(prev) === sig(sorted) ? prev : sorted;
      });
    } catch (e) {
      // Ignore transient poll errors on purpose.
    }
  }, [classId, user.role]);

  usePolling(pollTeacherRoster, { intervalMs: 0, enabled: user.role === 'teacher' });

  // Save indicator helpers
  const markSaving = () => {
    if (saveStatusTimeoutRef.current) clearTimeout(saveStatusTimeoutRef.current);
    setSaveStatus('saving');
  };
  const markSaved = () => {
    if (saveStatusTimeoutRef.current) clearTimeout(saveStatusTimeoutRef.current);
    setSaveStatus('saved');
    saveStatusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
  };
  const markSaveError = () => {
    if (saveStatusTimeoutRef.current) clearTimeout(saveStatusTimeoutRef.current);
    setSaveStatus('error');
    // Show the red error briefly, then fall back to the PERSISTENT amber
    // "sin guardar" state so the user always knows data is still pending
    // (it keeps retrying automatically every 15-20s until it succeeds).
    saveStatusTimeoutRef.current = setTimeout(() => setSaveStatus('unsaved'), 6000);
  };
  const markUnsaved = () => {
    if (saveStatusTimeoutRef.current) clearTimeout(saveStatusTimeoutRef.current);
    setSaveStatus('unsaved');
  };
  const hasPending = () =>
    Object.keys(pendingAttRef.current).length > 0 || Object.keys(pendingTaskRef.current).length > 0;

  // Flush buffered edits as ONE batch delta request per grid. Called on section
  // change, a 20s safety-net timer, and when leaving the screen. On failure the
  // pending edits are kept and the "sin guardar" warning stays so nothing is lost.
  const flushSaves = useCallback(async () => {
    if (flushingRef.current) return true;
    if (!hasPending()) return true;
    flushingRef.current = true;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    // Snapshot + clear so edits made during the request are not lost.
    const attSnap = pendingAttRef.current; pendingAttRef.current = {};
    const taskSnap = pendingTaskRef.current; pendingTaskRef.current = {};
    const attCells = [];
    Object.entries(attSnap).forEach(([sid, cols]) => Object.entries(cols).forEach(([cid, v]) => attCells.push({ student_id: sid, col_id: cid, value: v })));
    const taskCells = [];
    Object.entries(taskSnap).forEach(([sid, cols]) => Object.entries(cols).forEach(([cid, v]) => taskCells.push({ student_id: sid, col_id: cid, value: v })));
    markSaving();
    let ok = true;
    try {
      if (attCells.length) await axios.patch(`${API}/attendance-grid/${classId}/cells`, { cells: attCells }, { headers });
      if (taskCells.length) await axios.patch(`${API}/task-grid/${classId}/cells`, { cells: taskCells }, { headers });
      markSaved();
    } catch (e) {
      ok = false;
      // Merge the failed snapshot back so it retries and the warning persists.
      Object.entries(attSnap).forEach(([sid, cols]) => { pendingAttRef.current[sid] = { ...cols, ...(pendingAttRef.current[sid] || {}) }; });
      Object.entries(taskSnap).forEach(([sid, cols]) => { pendingTaskRef.current[sid] = { ...cols, ...(pendingTaskRef.current[sid] || {}) }; });
      markSaveError();
    } finally {
      flushingRef.current = false;
    }
    return ok;
  }, [classId]);
  const flushSavesRef = useRef(flushSaves);
  useEffect(() => { flushSavesRef.current = flushSaves; }, [flushSaves]);

  // Safety net: flush unsaved edits every 20s so nothing is lost even if the
  // teacher never changes section. + flush when leaving the screen; warn on
  // unload if there are still unsaved changes.
  useEffect(() => {
    if (user.role !== 'teacher') return undefined;
    const id = setInterval(() => { if (hasPending()) flushSavesRef.current(); }, 20000);
    const onBeforeUnload = (e) => {
      if (hasPending()) {
        // Best-effort save that survives unload.
        try {
          const token = localStorage.getItem('token');
          const attCells = [];
          Object.entries(pendingAttRef.current).forEach(([sid, cols]) => Object.entries(cols).forEach(([cid, v]) => attCells.push({ student_id: sid, col_id: cid, value: v })));
          const taskCells = [];
          Object.entries(pendingTaskRef.current).forEach(([sid, cols]) => Object.entries(cols).forEach(([cid, v]) => taskCells.push({ student_id: sid, col_id: cid, value: v })));
          if (attCells.length) fetch(`${API}/attendance-grid/${classId}/cells`, { method: 'PATCH', keepalive: true, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ cells: attCells }) });
          if (taskCells.length) fetch(`${API}/task-grid/${classId}/cells`, { method: 'PATCH', keepalive: true, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ cells: taskCells }) });
        } catch (err) { /* ignore */ }
        e.preventDefault();
        e.returnValue = 'Tienes cambios sin guardar.';
        return e.returnValue;
      }
      return undefined;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      clearInterval(id);
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (hasPending()) flushSavesRef.current(); // flush on unmount (navigating away)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, user.role]);

  // Passed down to child grid components (Civic, Eval) so all sections share the indicator
  const reportSave = (status) => {
    if (status === 'saving') markSaving();
    else if (status === 'saved') markSaved();
    else if (status === 'error') markSaveError();
    else if (status === 'unsaved') markUnsaved();
  };

  const fetchOrientadorJustifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/classes/${classId}/orientador-justifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrientadorJustifications(response.data);
    } catch (error) {
      console.error('Error fetching orientador justifications:', error);
    }
  };

  const fetchPendingJustifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/pending-justifications/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.length > 0) {
        setPendingJustifications(response.data);
        setShowJustificationNotification(true);
      }
    } catch (error) {
      console.error('Error fetching pending justifications:', error);
    }
  };

  const acknowledgeJustification = async (justificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/teacher/acknowledge-justification/${justificationId}?class_id=${classId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error acknowledging justification:', error);
    }
  };

  const handleDismissNotification = async () => {
    // Mark all as acknowledged
    for (const j of pendingJustifications) {
      await acknowledgeJustification(j.id);
    }
    setShowJustificationNotification(false);
    setPendingJustifications([]);
    // Refresh justifications
    fetchOrientadorJustifications();
  };

  // Check if a cell has orientador justification (JO)
  const hasOrientadorJustification = (studentId, dateColumn) => {
    const student = students.find(s => s.id === studentId);
    if (!student) return false;
    
    const dateStr = dateColumn.date; // Format: YYYY-MM-DD
    const label = dateColumn.label; // Format: DD/MM
    const studentNameUpper = student.name.toUpperCase().trim();
    
    return orientadorJustifications.some(j => {
      if (j.student_name.toUpperCase().trim() !== studentNameUpper) return false;
      if (!j.dates || j.dates.length === 0) return false;
      // Camino B: justificación por horas → cubre solo ciertas clases. Si esta
      // clase no está en la lista, NO aplica JO (queda editable).
      if (Array.isArray(j.covered_class_ids) && j.covered_class_ids.length > 0 && !j.covered_class_ids.includes(classId)) return false;
      // Check direct date match
      if (dateStr && j.dates.includes(dateStr)) return true;
      // Also check by converting j.dates to DD/MM and comparing with label
      if (label) {
        return j.dates.some(d => {
          const parts = d.split('-');
          if (parts.length === 3) {
            const formatted = `${parts[2]}/${parts[1]}`;
            return formatted === label;
          }
          return false;
        });
      }
      return false;
    });
  };

  // Asistencias por PARCIAL (alineadas a los separadores de la grilla de asistencias),
  // reutilizadas por la tabla "Calificación Total por Parcial" de Evaluación.
  // Estructura: { [studentId]: [{ present, registered, pct }, ...] } (índice = parcial).
  const attendanceByParcial = useMemo(() => {
    const cols = attendanceGrid?.columns || [];
    const blocks = buildAttColumnsWithCumulative(cols)
      .filter(c => c._isAttCumulative)
      .map(c => c._colIds || []);
    const map = {};
    (students || []).forEach(st => {
      const rec = (attendanceGrid?.records || {})[st.id] || {};
      map[st.id] = blocks.map(colIds => calcAttCounts(rec, colIds, hasOrientadorJustification, st.id, cols));
    });
    return map;
  }, [attendanceGrid, students, orientadorJustifications]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAvailableGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchAllClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/all-classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const handleImportGroup = async (groupId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/classes/${classId}/import-group/${groupId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setImportGroupDialog(false);
      fetchClassData(); // Refresh to get updated bulk_import_done
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar grupo');
    }
  };

  const handleCopyStudents = async (sourceClassId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/classes/copy-students`, {
        source_class_id: sourceClassId,
        target_class_id: classId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setCopyStudentsDialog(false);
      fetchClassData(); // Refresh to get updated bulk_import_done
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al copiar alumnos');
    }
  };

  const handleEditStudent = (student) => {
    setStudentToEdit(student);
    setEditStudentName(student.name);
    setEditStudentDialog(true);
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/students/${studentToEdit.id}`, {
        name: editStudentName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Alumno actualizado');
      setEditStudentDialog(false);
      fetchStudents();
    } catch (error) {
      toast.error('Error al actualizar alumno');
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este alumno de la clase?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/classes/${classId}/students/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Alumno eliminado de la clase');
      fetchStudents();
    } catch (error) {
      toast.error('Error al eliminar alumno');
    }
  };

  const handleDeleteDateColumn = async (columnId) => {
    if (!columnId) {
      toast.error('Error: No se puede identificar la columna');
      return;
    }
    
    if (!window.confirm('¿Estás seguro de eliminar esta fecha y todos sus registros?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      markSaving();
      
      // Filter out the column by its id
      const newColumns = attendanceGrid.columns.filter(col => col.id !== columnId);
      
      // Create a clean copy of records without the deleted column
      const newRecords = {};
      Object.keys(attendanceGrid.records || {}).forEach(studentId => {
        const studentRecord = { ...(attendanceGrid.records[studentId] || {}) };
        delete studentRecord[columnId];
        newRecords[studentId] = studentRecord;
      });
      
      // Save to backend ('intentional' bypasses the anti-wipe guard for a real delete)
      await axios.put(`${API}/attendance-grid/${classId}?intentional=true`, {
        columns: newColumns,
        records: newRecords
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Force reload from backend to ensure UI sync
      const response = await axios.get(`${API}/attendance-grid/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAttendanceGrid(response.data);
      markSaved();
      toast.success('Columna eliminada');
    } catch (error) {
      console.error('Error al eliminar fecha:', error);
      markSaveError();
      toast.error('Error al eliminar fecha');
    }
  };

  const handleDeleteTaskColumn = async (taskId) => {
    if (!taskId) {
      toast.error('Error: No se puede identificar la tarea');
      return;
    }
    
    if (!window.confirm('¿Estás seguro de eliminar esta tarea y todos sus registros?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      markSaving();
      // Dedicated delete endpoint: unambiguous, works even for the last remaining task.
      await axios.delete(`${API}/task-grid/${classId}/column/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Force reload from backend
      const response = await axios.get(`${API}/task-grid/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTaskGrid(response.data);
      markSaved();
      toast.success('Tarea eliminada');
    } catch (error) {
      console.error('Error al eliminar tarea:', error);
      markSaveError();
      toast.error('Error al eliminar tarea');
    }
  };

  const fetchClassData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/classes/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassData(response.data);
    } catch (error) {
      console.error('Error fetching class:', error);
      setLoadError(true);
    }
  };

  const fetchStudents = async (silent = false) => {
    try {
      const token = localStorage.getItem('token');
      // Conditional GET: unchanged roster => 304 (0 bytes), served from cache.
      const studentsRes = await pollGet(`${API}/classes/${classId}/students`, {
        key: `students-${classId}`, conditional: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!studentsRes.data) return; // 304 with no cached data yet: keep current
      const sortedStudents = [...studentsRes.data].sort((a, b) => a.name.localeCompare(b.name));
      setStudents(sortedStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
      // Background refreshes (tab switch / window focus) must never trip the
      // blocking load-error screen on a transient network blip.
      if (!silent) setLoadError(true);
    }
  };

  const fetchAttendanceGrid = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await pollGet(`${API}/attendance-grid/${classId}`, {
        key: `att-${classId}`, conditional: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) setAttendanceGrid(response.data);
    } catch (error) {
      console.error('Error fetching attendance grid:', error);
      setLoadError(true);
    }
  };

  const fetchTaskGrid = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await pollGet(`${API}/task-grid/${classId}`, {
        key: `task-${classId}`, conditional: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setTaskGrid(response.data);
        setTaskNotes(response.data.notes || {});
        if (response.data.task_weights) {
          setTaskWeights(response.data.task_weights);
        }
      }
    } catch (error) {
      console.error('Error fetching task grid:', error);
      setLoadError(true);
    }
  };

  const saveTaskWeights = async (newWeights) => {
    try {
      const token = localStorage.getItem('token');
      markSaving();
      await axios.put(`${API}/task-grid/${classId}/weights`, { task_weights: newWeights }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      markSaved();
    } catch (error) {
      console.error('Error saving weights:', error);
      markSaveError();
    }
  };

  const handleWeightChange = (parcial, value) => {
    const cleaned = normalizeWeightInput(value);
    if (cleaned !== null) {
      const newWeights = { ...taskWeights, [parcial]: cleaned };
      setTaskWeights(newWeights);
      saveTaskWeights(newWeights);
    }
  };

  const handleAttWeightChange = async (parcial, value) => {
    const cleaned = normalizeWeightInput(value);
    if (cleaned === null) return;
    const newWeights = { ...attWeights, [parcial]: cleaned };
    setAttWeights(newWeights);
    try {
      const token = localStorage.getItem('token');
      markSaving();
      await axios.put(`${API}/attendance-grid/${classId}/weights`, { att_weights: newWeights }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      markSaved();
    } catch (error) {
      console.error('Error saving attendance weights:', error);
      markSaveError();
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Create student
      const studentRes = await axios.post(`${API}/students`, {
        name: studentName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Add to class
      await axios.post(`${API}/classes/${classId}/students`, {
        student_id: studentRes.data.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Alumno agregado exitosamente');
      setAddStudentDialog(false);
      setStudentName('');
      fetchStudents();
    } catch (error) {
      toast.error('Error al agregar alumno');
    }
  };

  const handleAttendanceChange = useCallback(async (studentId, dateKey, value) => {
    // Validate input
    if (value !== '' && !['1', '0', 'J', 'j', 'R', 'r'].includes(value)) {
      return;
    }

    const normalizedValue = value.toUpperCase();
    markEditActivity();

    // Read current grid from the ref so this callback stays stable (useCallback).
    // A stable onChange keeps the memoized AttendanceCell from re-rendering the
    // whole grid on every keystroke/commit.
    const prevGrid = attendanceGridRef.current;
    const newRecords = {
      ...prevGrid.records,
      [studentId]: {
        ...(prevGrid.records[studentId] || {}),
        [dateKey]: normalizedValue
      }
    };

    setAttendanceGrid(prev => ({ ...prev, records: newRecords }));

    // Buffer the change; it will be saved as a batch on section change / 20s
    // safety net / on leaving the screen. No per-keystroke network request.
    if (!pendingAttRef.current[studentId]) pendingAttRef.current[studentId] = {};
    pendingAttRef.current[studentId][dateKey] = normalizedValue;
    markUnsaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const handleTaskChange = useCallback(async (studentId, taskKey, value) => {
    // Validate input: number 0-100 or empty
    const cleaned = value.replace('%', '').trim();
    if (cleaned !== '') {
      const num = parseInt(cleaned, 10);
      if (isNaN(num) || num < 0 || num > 100) return;
    }

    // Use the ref for current records so this callback can stay stable (useCallback).
    const prevRecords = taskGridRef.current.records || {};
    markEditActivity();
    const newRecords = {
      ...prevRecords,
      [studentId]: {
        ...(prevRecords[studentId] || {}),
        [taskKey]: cleaned
      }
    };

    setTaskGrid(prev => ({ ...prev, records: newRecords }));

    // Buffer the change; saved as a batch on section change / 20s / leaving.
    if (!pendingTaskRef.current[studentId]) pendingTaskRef.current[studentId] = {};
    pendingTaskRef.current[studentId][taskKey] = cleaned;
    markUnsaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  // Open the shared note/edit popover for a filled task cell. Reads current data
  // from refs so the callback identity is stable (keeps TaskCell memo effective).
  const openNoteDialog = useCallback((studentId, colId) => {
    setNoteDialog({
      studentId,
      colId,
      initialValue: (taskGridRef.current.records[studentId] || {})[colId] || '',
      initialText: (taskNotesRef.current[studentId] || {})[colId] || ''
    });
  }, []);

  const [attNoteDialog, setAttNoteDialog] = useState(null);
  const openAttNoteDialog = useCallback((studentId, colId) => {
    const g = attendanceGridRef.current;
    setAttNoteDialog({
      studentId,
      colId,
      initialValue: (g.records[studentId] || {})[colId] || '',
      initialText: ((g.notes || {})[studentId] || {})[colId] || ''
    });
  }, []);

  const handleAttNoteSave = useCallback(async (studentId, colId, value, text) => {
    const token = localStorage.getItem('token');
    const currentValue = (attendanceGridRef.current.records[studentId] || {})[colId] || '';
    if (value !== currentValue) {
      handleAttendanceChange(studentId, colId, value);
      await flushSaves();
    }
    await axios.put(`${API}/attendance-grid/${classId}/note`, {
      student_id: studentId, col_id: colId, text
    }, { headers: { Authorization: `Bearer ${token}` } });
    setAttendanceGrid(prev => {
      const notes = { ...(prev.notes || {}), [studentId]: { ...((prev.notes || {})[studentId] || {}) } };
      if (text.trim()) notes[studentId][colId] = text.trim();
      else delete notes[studentId][colId];
      return { ...prev, notes };
    });
    toast.success('Guardado');
    setAttNoteDialog(null);
  }, [classId, handleAttendanceChange, flushSaves]);

  // Persist grade (if changed) + note. Called by the isolated TaskNoteDialog.
  const handleNoteSave = useCallback(async (studentId, colId, value, text) => {
    const token = localStorage.getItem('token');
    const currentValue = (taskGridRef.current.records[studentId] || {})[colId] || '';
    if (value !== currentValue) {
      await handleTaskChange(studentId, colId, value);
    }
    await axios.put(`${API}/task-grid/${classId}/note`, {
      student_id: studentId, col_id: colId, text
    }, { headers: { Authorization: `Bearer ${token}` } });

    setTaskNotes(prev => {
      const next = { ...prev, [studentId]: { ...(prev[studentId] || {}) } };
      if (text.trim()) next[studentId][colId] = text;
      else delete next[studentId][colId];
      return next;
    });
    toast.success('Guardado');
    setNoteDialog(null);
  }, [classId, handleTaskChange]);


  // Helper function to sort columns chronologically while preserving separator positions
  // Dates are sorted chronologically, but separators stay in their relative positions
  const sortColumnsChronologically = (columns) => {
    // If there are no separators, just sort all dates
    const hasSeparators = columns.some(col => col.isSeparator);
    if (!hasSeparators) {
      return [...columns].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB;
      });
    }
    
    // With separators: sort date columns but keep separators at their positions
    // First, extract all date columns and sort them
    const dateColumns = columns.filter(col => !col.isSeparator);
    const sortedDates = [...dateColumns].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });
    
    // Now rebuild the array, putting dates back in order and keeping separators in place
    const result = [];
    let dateIndex = 0;
    
    for (const col of columns) {
      if (col.isSeparator) {
        result.push(col);
      } else {
        result.push(sortedDates[dateIndex]);
        dateIndex++;
      }
    }
    
    return result;
  };

  // Adds a "Suspensión de Clase" column: informative only, non-editable cells
  // (every cell shows a red X), never counts toward the attendance %. Deletable.
  const handleAddSuspension = async (e) => {
    e.preventDefault();
    const date = suspensionForm.date;
    const motivo = (suspensionForm.motivo || '').trim();
    if (!date) { toast.error('Selecciona la fecha de la suspensión'); return; }
    if (!motivo) { toast.error('Escribe el motivo de la suspensión'); return; }
    const columnId = `susp_${Date.now()}`;
    const newColumn = {
      id: columnId,
      date,
      isSuspension: true,
      motivo,
      label: date.split('-').reverse().slice(0, 2).join('/')
    };
    const newColumns = sortColumnsChronologically([...attendanceGrid.columns, newColumn]);
    try {
      const token = localStorage.getItem('token');
      markSaving();
      await axios.put(`${API}/attendance-grid/${classId}`, {
        columns: newColumns,
        records: attendanceGrid.records
      }, { headers: { Authorization: `Bearer ${token}` } });
      const res = await axios.get(`${API}/attendance-grid/${classId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAttendanceGrid(res.data);
      markSaved();
      setSuspensionDialog(false);
      setSuspensionForm({ date: '', motivo: '' });
      toast.success('Suspensión de clase registrada');
    } catch (error) {
      markSaveError();
      toast.error('Error al registrar la suspensión');
    }
  };

  // ---- Suspension conflict checks for Tareas ----
  const getSuspensionDates = () => (attendanceGrid.columns || []).filter(c => c.isSuspension).map(c => c.date);

  const findTaskConflict = (columns) => {
    const susp = getSuspensionDates();
    return (columns || []).find(c => !c.isSeparator && c.date && susp.includes(c.date) && c.suspAckFor !== c.date) || null;
  };

  const openTaskConflictIfAny = () => {
    setConflictReprogram(false);
    setConflictNewDate('');
    setConflictTask(findTaskConflict(taskGrid.columns));
  };

  const saveTaskColumns = async (newColumns) => {
    const token = localStorage.getItem('token');
    markSaving();
    await axios.put(`${API}/task-grid/${classId}`, { columns: newColumns, records: taskGrid.records }, { headers: { Authorization: `Bearer ${token}` } });
    const res = await axios.get(`${API}/task-grid/${classId}`, { headers: { Authorization: `Bearer ${token}` } });
    setTaskGrid(res.data);
    markSaved();
    return res.data.columns;
  };

  const handleKeepTask = async () => {
    const col = conflictTask;
    const newColumns = (taskGrid.columns || []).map(c => c.id === col.id ? { ...c, suspAckFor: c.date } : c);
    try {
      const saved = await saveTaskColumns(newColumns);
      setConflictReprogram(false); setConflictNewDate('');
      setConflictTask(findTaskConflict(saved));
    } catch (e) { toast.error('Error al guardar'); }
  };

  const handleReprogramTask = async () => {
    const col = conflictTask;
    const nd = conflictNewDate;
    if (!nd) { toast.error('Selecciona la nueva fecha'); return; }
    if (getSuspensionDates().includes(nd)) { toast.error('Esa fecha también es un día de suspensión. Elige otra.'); return; }
    const prefix = (col.label || '').split(' - ')[0];
    const newLabel = `${prefix} - ${nd.split('-').reverse().slice(0, 2).join('/')}`;
    const newColumns = (taskGrid.columns || []).map(c => c.id === col.id ? { ...c, date: nd, label: newLabel, suspAckFor: undefined } : c);
    try {
      const saved = await saveTaskColumns(newColumns);
      toast.success('Tarea reprogramada');
      setConflictReprogram(false); setConflictNewDate('');
      setConflictTask(findTaskConflict(saved));
    } catch (e) { toast.error('Error al reprogramar'); }
  };
  // Multiple columns for the SAME date are allowed (a class may meet more than once a day).
  const addAttendanceDate = async (dateStr) => {
    const columnId = `date_${Date.now()}_${attendanceGridRef.current.columns.length}`;
    const newColumns = sortColumnsChronologically([
      ...attendanceGridRef.current.columns,
      {
        id: columnId,
        date: dateStr,
        label: dateStr.split('-').reverse().slice(0, 2).join('/')
      }
    ]);
    const token = localStorage.getItem('token');
    markSaving();
    await axios.put(`${API}/attendance-grid/${classId}`, {
      columns: newColumns,
      records: attendanceGridRef.current.records
    }, { headers: { Authorization: `Bearer ${token}` } });
    const gridResponse = await axios.get(`${API}/attendance-grid/${classId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setAttendanceGrid(gridResponse.data);
    markSaved();
    // Re-check orientador justifications so the "JO"/notification shows immediately
    // when the newly added date matches an existing justification (no need to leave and re-enter).
    fetchOrientadorJustifications();
    if (user.role === 'teacher') fetchPendingJustifications();
  };

  const handleAddDate = async (e) => {
    e.preventDefault();
    if (!newDate) return;
    try {
      await addAttendanceDate(newDate);
      toast.success('Fecha agregada');
      setAddDateDialog(false);
      setNewDate('');
    } catch (error) {
      console.error('Error al agregar fecha:', error);
      markSaveError();
      toast.error('Error al agregar fecha');
    }
  };

  // "HOY": adds a column with today's local date directly, without opening the calendar.
  const handleAddToday = async () => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    try {
      await addAttendanceDate(todayStr);
      toast.success('Columna de hoy agregada');
    } catch (error) {
      console.error('Error al agregar la fecha de hoy:', error);
      markSaveError();
      toast.error('Error al agregar la fecha de hoy');
    }
  };

  // "Asistencia General": adds a column for today and pre-marks '1' (asistió) for every
  // student EXCEPT those who already have an orientador justification (JO) on that date,
  // whose cells are left untouched so the JO logic keeps applying. The column behaves like
  // any other (editable, counted in the % and nomenclatura).
  const handleAsistenciaGeneral = async () => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const alreadyToday = (attendanceGridRef.current.columns || [])
      .some(c => !c.isSeparator && c.date === todayStr);
    if (alreadyToday) {
      if (!window.confirm('Ya existe una columna con la fecha de hoy. ¿Deseas crear otra columna de asistencia general de todos modos?')) return;
    } else {
      if (!window.confirm('Se marcará asistencia (1) a todos los alumnos con la fecha de hoy, excepto los que tengan justificante del orientador (JO). ¿Continuar?')) return;
    }

    const columnId = `date_${Date.now()}_${attendanceGridRef.current.columns.length}`;
    const newColumn = {
      id: columnId,
      date: todayStr,
      label: todayStr.split('-').reverse().slice(0, 2).join('/')
    };
    const newColumns = sortColumnsChronologically([
      ...attendanceGridRef.current.columns,
      newColumn
    ]);

    // Pre-fill '1' for every student without a JO on today's date.
    const newRecords = {};
    Object.keys(attendanceGridRef.current.records || {}).forEach(sid => {
      newRecords[sid] = { ...(attendanceGridRef.current.records[sid] || {}) };
    });
    students.forEach(s => {
      if (!newRecords[s.id]) newRecords[s.id] = {};
      const isJO = hasOrientadorJustification(s.id, newColumn);
      if (!isJO) {
        newRecords[s.id][columnId] = '1';
      }
    });

    try {
      const token = localStorage.getItem('token');
      markSaving();
      await axios.put(`${API}/attendance-grid/${classId}`, {
        columns: newColumns,
        records: newRecords
      }, { headers: { Authorization: `Bearer ${token}` } });
      const gridResponse = await axios.get(`${API}/attendance-grid/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendanceGrid(gridResponse.data);
      markSaved();
      fetchOrientadorJustifications();
      if (user.role === 'teacher') fetchPendingJustifications();
      toast.success('Asistencia general registrada');
    } catch (error) {
      console.error('Error en asistencia general:', error);
      markSaveError();
      toast.error('Error al registrar la asistencia general');
    }
  };

  // "Falta General": simétrico a "Asistencia General". Agrega una columna con la fecha
  // de hoy y marca '0' (falta) a todos los alumnos EXCEPTO los que tengan justificante
  // del orientador (JO) en esa fecha, cuyas celdas se dejan intactas. La columna es
  // editable y cuenta en el % de inasistencias como cualquier otra.
  const handleFaltaGeneral = async () => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const alreadyToday = (attendanceGridRef.current.columns || [])
      .some(c => !c.isSeparator && c.date === todayStr);
    if (alreadyToday) {
      if (!window.confirm('Ya existe una columna con la fecha de hoy. ¿Deseas crear otra columna de falta general de todos modos?')) return;
    } else {
      if (!window.confirm('Se marcará falta (0) a todos los alumnos con la fecha de hoy, excepto los que tengan justificante del orientador (JO). ¿Continuar?')) return;
    }

    const columnId = `date_${Date.now()}_${attendanceGridRef.current.columns.length}`;
    const newColumn = {
      id: columnId,
      date: todayStr,
      label: todayStr.split('-').reverse().slice(0, 2).join('/')
    };
    const newColumns = sortColumnsChronologically([
      ...attendanceGridRef.current.columns,
      newColumn
    ]);

    // Pre-fill '0' for every student without a JO on today's date.
    const newRecords = {};
    Object.keys(attendanceGridRef.current.records || {}).forEach(sid => {
      newRecords[sid] = { ...(attendanceGridRef.current.records[sid] || {}) };
    });
    students.forEach(s => {
      if (!newRecords[s.id]) newRecords[s.id] = {};
      const isJO = hasOrientadorJustification(s.id, newColumn);
      if (!isJO) {
        newRecords[s.id][columnId] = '0';
      }
    });

    try {
      const token = localStorage.getItem('token');
      markSaving();
      await axios.put(`${API}/attendance-grid/${classId}`, {
        columns: newColumns,
        records: newRecords
      }, { headers: { Authorization: `Bearer ${token}` } });
      const gridResponse = await axios.get(`${API}/attendance-grid/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendanceGrid(gridResponse.data);
      markSaved();
      fetchOrientadorJustifications();
      if (user.role === 'teacher') fetchPendingJustifications();
      toast.success('Falta general registrada');
    } catch (error) {
      console.error('Error en falta general:', error);
      markSaveError();
      toast.error('Error al registrar la falta general');
    }
  };

  // Calcula numero de tarea por parcial: despues de cada separador se reinicia a 1
  const getTaskDisplayNumber = (columns, targetIdx) => {
    let count = 0;
    for (let i = 0; i <= targetIdx; i++) {
      if (columns[i].isSeparator) { count = 0; }
      else { count++; }
    }
    return `T${count}`;
  };

  // Calcula el siguiente numero de tarea para el parcial actual
  const getNextTaskNumber = (columns) => {
    let count = 0;
    for (const col of columns) {
      if (col.isSeparator) { count = 0; }
      else { count++; }
    }
    return `T${count + 1}`;
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    // Validar que la fecha no sea pasada
    if (!newTask.date) {
      toast.error('Selecciona una fecha para la tarea');
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const taskDate = new Date(newTask.date + 'T00:00:00');
    if (isNaN(taskDate.getTime()) || taskDate < today) {
      toast.error('No se pueden agregar tareas con fechas anteriores a hoy');
      return;
    }
    const taskId = `task_${Date.now()}`;
    const autoNumber = getNextTaskNumber(taskGrid.columns);
    const newColumns = [
      ...taskGrid.columns,
      { 
        id: taskId,
        date: newTask.date,
        number: autoNumber,
        description: newTask.description,
        label: `${autoNumber} - ${newTask.date.split('-').reverse().slice(0,2).join('/')}`
      }
    ];
    
    try {
      const token = localStorage.getItem('token');
      markSaving();
      const response = await axios.put(`${API}/task-grid/${classId}`, {
        columns: newColumns,
        records: taskGrid.records
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Task added response:', response.data);
      
      // Reload grid from backend
      const gridResponse = await axios.get(`${API}/task-grid/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Reloaded task grid:', gridResponse.data);
      setTaskGrid(gridResponse.data);
      
      markSaved();
      toast.success('Tarea agregada');
      setAddTaskDialog(false);
      setNewTask({ date: '', description: '' });
    } catch (error) {
      console.error('Error al agregar tarea:', error);
      markSaveError();
      toast.error('Error al agregar tarea: ' + (error.response?.data?.detail || error.message));
    }
  };


  const openEditTask = (col) => {
    setEditingTaskCol(col);
    setEditTaskForm({ date: col.date || '', description: col.description || '' });
    setEditTaskDialog(true);
  };

  const handleEditTask = async (e) => {
    e.preventDefault();
    if (!editingTaskCol) return;
    const updatedColumns = taskGrid.columns.map(c => {
      if (c.id !== editingTaskCol.id) return c;
      const newNumber = getTaskDisplayNumber(taskGrid.columns, taskGrid.columns.indexOf(c));
      return {
        ...c,
        date: editTaskForm.date,
        description: editTaskForm.description,
        label: `${newNumber} - ${editTaskForm.date.split('-').reverse().slice(0, 2).join('/')}`
      };
    });
    try {
      const token = localStorage.getItem('token');
      markSaving();
      await axios.put(`${API}/task-grid/${classId}`, {
        columns: updatedColumns,
        records: taskGrid.records
      }, { headers: { Authorization: `Bearer ${token}` } });
      const gridResponse = await axios.get(`${API}/task-grid/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskGrid(gridResponse.data);
      markSaved();
      toast.success('Tarea actualizada');
      setEditTaskDialog(false);
      setEditingTaskCol(null);
    } catch (error) {
      markSaveError();
      toast.error('Error al editar tarea');
    }
  };

  // Function to add partial separator
  const handleAddSeparator = async (e) => {
    e.preventDefault();
    const separatorId = `separator_${Date.now()}`;
    
    let label;
    if (separatorType === 'task') {
      // Auto-name for task separators
      const existingTaskSeps = taskGrid.columns.filter(c => c.isSeparator).length;
      if (existingTaskSeps >= 3) {
        toast.error('Máximo 3 separadores de parcial permitidos');
        setAddSeparatorDialog(false);
        return;
      }
      const names = ['FIN DE PRIMER PARCIAL', 'FIN DEL SEGUNDO PARCIAL', 'FIN DEL TERCER PARCIAL'];
      label = names[existingTaskSeps];
    } else {
      label = separatorLabel || 'Parcial';
    }
    
    const separatorColumn = {
      id: separatorId,
      isSeparator: true,
      label: label,
      date: null
    };
    
    try {
      const token = localStorage.getItem('token');
      markSaving();
      
      if (separatorType === 'attendance') {
        const newColumns = [...attendanceGrid.columns, separatorColumn];
        await axios.put(`${API}/attendance-grid/${classId}`, {
          columns: newColumns,
          records: attendanceGrid.records
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const gridResponse = await axios.get(`${API}/attendance-grid/${classId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAttendanceGrid(gridResponse.data);
      } else {
        const newColumns = [...taskGrid.columns, separatorColumn];
        await axios.put(`${API}/task-grid/${classId}`, {
          columns: newColumns,
          records: taskGrid.records
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const gridResponse = await axios.get(`${API}/task-grid/${classId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTaskGrid(gridResponse.data);
      }
      
      markSaved();
      toast.success('Separador de parcial agregado');
      setAddSeparatorDialog(false);
      setSeparatorLabel('');
    } catch (error) {
      console.error('Error al agregar separador:', error);
      markSaveError();
      toast.error('Error al agregar separador');
    }
  };

  // Function to move separator left or right
  const handleMoveSeparator = async (separatorId, direction, gridType) => {
    try {
      const token = localStorage.getItem('token');
      const grid = gridType === 'attendance' ? attendanceGrid : taskGrid;
      const columns = [...grid.columns];
      
      // Find current index of separator
      const currentIndex = columns.findIndex(col => col.id === separatorId);
      if (currentIndex === -1) return;
      
      // Calculate new index
      let newIndex;
      if (direction === 'left') {
        newIndex = currentIndex - 1;
        if (newIndex < 0) return; // Can't move further left
      } else {
        newIndex = currentIndex + 1;
        if (newIndex >= columns.length) return; // Can't move further right
      }
      
      // Swap positions
      const temp = columns[currentIndex];
      columns[currentIndex] = columns[newIndex];
      columns[newIndex] = temp;
      
      markSaving();
      // Save to backend
      if (gridType === 'attendance') {
        const relabeled = relabelAttSeparators(columns);
        await axios.put(`${API}/attendance-grid/${classId}`, {
          columns: relabeled,
          records: grid.records
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const gridResponse = await axios.get(`${API}/attendance-grid/${classId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAttendanceGrid(gridResponse.data);
      } else {
        const relabeled = relabelTaskSeparators(columns);
        await axios.put(`${API}/task-grid/${classId}`, {
          columns: relabeled,
          records: grid.records
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const gridResponse = await axios.get(`${API}/task-grid/${classId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTaskGrid(gridResponse.data);
      }
      markSaved();
    } catch (error) {
      console.error('Error al mover separador:', error);
      markSaveError();
      toast.error('Error al mover separador');
    }
  };

  const filteredStudents = students.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Teacher notification functions
  const toggleStudentNotifSelection = (studentName) => {
    setSelectedStudentsForNotif(prev => 
      prev.includes(studentName) ? prev.filter(n => n !== studentName) : [...prev, studentName]
    );
  };

  const toggleAllStudentsNotif = () => {
    if (selectedStudentsForNotif.length === filteredStudents.length) {
      setSelectedStudentsForNotif([]);
    } else {
      setSelectedStudentsForNotif(filteredStudents.map(s => s.name));
    }
  };

  const openTeacherNotifForm = (type) => {
    if (selectedStudentsForNotif.length === 0) {
      toast.error('Selecciona al menos un alumno');
      return;
    }
    setTeacherNotifType(type);
  };

  const closeTeacherNotifForm = () => {
    setTeacherNotifType(null);
    setTeacherCitatorioForm({ fecha: '', hora: '', lugar: '', motivo: '' });
    setTeacherAvisoForm({ mensaje: '' });
  };

  const sendTeacherCitatorio = async () => {
    const f = teacherCitatorioForm;
    if (!f.fecha || !f.hora || !f.lugar || !f.motivo) {
      toast.error('Todos los campos son requeridos');
      return;
    }
    setSendingTeacherNotif(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/teacher/notifications/citatorio`, {
        class_id: classId,
        student_names: selectedStudentsForNotif,
        ...f
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Citatorio enviado correctamente');
      closeTeacherNotifForm();
      setSelectedStudentsForNotif([]);
      fetchSentNotifications();
    } catch (error) {
      toast.error('Error al enviar el citatorio');
    } finally {
      setSendingTeacherNotif(false);
    }
  };

  const sendTeacherAviso = async () => {
    if (!teacherAvisoForm.mensaje.trim()) {
      toast.error('El mensaje del aviso es requerido');
      return;
    }
    setSendingTeacherNotif(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/teacher/notifications/aviso`, {
        class_id: classId,
        student_names: selectedStudentsForNotif,
        mensaje: teacherAvisoForm.mensaje
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Aviso enviado correctamente');
      closeTeacherNotifForm();
      setSelectedStudentsForNotif([]);
      fetchSentNotifications();
    } catch (error) {
      toast.error('Error al enviar el aviso');
    } finally {
      setSendingTeacherNotif(false);
    }
  };

  const fetchSentNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/notifications/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSentNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching sent notifications:', error);
    }
  };

  const deleteTeacherNotification = async (notifId) => {
    if (!window.confirm('¿Eliminar esta notificación? Se eliminará del tablero de los tutores.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/teacher/notifications/${notifId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notificación eliminada');
      fetchSentNotifications();
    } catch (error) {
      toast.error('Error al eliminar la notificación');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6 md:p-8 lg:p-12">
          <div className="text-center py-12 text-slate-600">Cargando...</div>
        </div>
      </Layout>
    );
  }

  // DATA INTEGRITY GUARD (frontend): if any core data failed to load, block the
  // editing UI entirely so a stale/empty grid can never overwrite saved data.
  if (loadError) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6 md:p-8 lg:p-12">
          <div className="max-w-md mx-auto py-12" data-testid="class-load-error">
            <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
              <h2 className="text-xl font-bold text-red-700 mb-2">No se pudieron cargar los datos</h2>
              <p className="text-slate-600 mb-6">
                Para proteger la información, la edición está bloqueada. Por favor recarga la página o vuelve a iniciar sesión.
              </p>
              <Button
                onClick={() => window.location.reload()}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="reload-class-btn"
              >
                Recargar página
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const isReadOnly = user.role === 'tutor';

  return (
    <Layout user={user} onLogout={onLogout}>
      {/* Floating save indicator (only for teachers who capture data) — draggable */}
      {!isReadOnly && saveStatus !== 'idle' && (
        <div
          data-testid="save-status-indicator"
          data-status={saveStatus}
          onPointerDown={handleIndicatorPointerDown}
          style={indicatorPos
            ? { left: indicatorPos.x, top: indicatorPos.y, right: 'auto', bottom: 'auto', touchAction: 'none' }
            : { touchAction: 'none' }}
          className={`fixed bottom-5 right-5 z-50 flex items-start gap-2 pl-2 pr-4 py-2.5 rounded-2xl shadow-lg text-sm font-semibold transition-shadow duration-300 max-w-[85vw] sm:max-w-sm leading-snug cursor-move select-none active:shadow-xl ${
            saveStatus === 'saving' ? 'bg-slate-800 text-white' :
            saveStatus === 'saved' ? 'bg-green-600 text-white' :
            saveStatus === 'unsaved' ? 'bg-amber-500 text-white' :
            'bg-red-600 text-white'
          }`}
        >
          <GripVertical className="w-4 h-4 shrink-0 mt-0.5 opacity-70" data-testid="save-status-drag-handle" />
          <div className="flex items-start gap-2">
            {saveStatus === 'saving' && (<><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</>)}
            {saveStatus === 'saved' && (<><CheckCircle2 className="w-4 h-4" /> Guardado</>)}
            {saveStatus === 'unsaved' && (<><AlertCircle className="w-4 h-4 shrink-0" /> <span>Se guardarán los cambios al cambiar de sección y/o de manera automática cada 20 segundos. No cierres la app ni apagues la pantalla hasta ver "Guardado".</span></>)}
            {saveStatus === 'error' && (<><AlertCircle className="w-4 h-4 shrink-0" /> <span>Error al guardar. Revisa tu conexión. No cierres la app ni apagues la pantalla hasta ver "Guardado" (se reintenta solo).</span></>)}
          </div>
        </div>
      )}
      <div className="p-6 md:p-8 lg:p-12">
        {/* Notification Modal for Pending Justifications */}
        <Dialog open={showJustificationNotification} onOpenChange={setShowJustificationNotification}>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-600">
                <Bell className="w-5 h-5" />
                Notificación de Justificaciones del Orientador
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                El orientador ha registrado las siguientes justificaciones para alumnos en esta clase:
              </p>
              <div className="max-h-64 overflow-y-auto space-y-3">
                {pendingJustifications.map((j, idx) => (
                  <div key={idx} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">{j.student_name}</p>
                        <p className="text-sm text-slate-600 mt-1">
                          <span className="font-medium">Motivo:</span> {j.reason}
                        </p>
                        <p className="text-sm text-slate-600">
                          <span className="font-medium">Fechas justificadas:</span>{' '}
                          {j.dates.map(d => d.split('-').reverse().slice(0, 2).join('/')).join(', ')}
                        </p>
                        {Array.isArray(j.covered_class_ids) && j.covered_class_ids.length > 0 && (
                          <p className={`text-xs mt-1 font-medium ${j.applies_to_this_class ? 'text-blue-700' : 'text-emerald-700'}`}>
                            {j.applies_to_this_class
                              ? '⏱ Justificación por horas: aplica a ESTA clase (se marcará JO).'
                              : '⏱ Justificación por horas: NO aplica a esta clase (queda editable, pasa lista normal).'}
                          </p>
                        )}
                        <p className="text-xs text-slate-500 mt-1">
                          Registrado por: {j.orientador_name}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Nota:</strong> Las celdas de asistencia para estas fechas y alumnos mostrarán "JO" (azul) y no podrán ser editadas.
                </p>
              </div>
              <Button 
                onClick={handleDismissNotification} 
                className="w-full bg-blue-500 hover:bg-blue-600"
                data-testid="acknowledge-justifications-btn"
              >
                Entendido
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Button
          onClick={() => navigate(user.role === 'teacher' ? '/classes' : '/tutor/classes')}
          data-testid="back-button"
          className="mb-6 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-lg"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Clases
        </Button>

        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {classData?.subject}
            </h1>
            <p className="text-base text-slate-600">Grupo: {classData?.group}</p>
          </div>
          <Button
            onClick={() => {
              // Refresh the app WITHOUT logging the user out: keep the auth
              // session (token + user) and only drop other cached app-data
              // (e.g. collapsed-separator keys), then reload the page.
              const token = localStorage.getItem('token');
              const userStr = localStorage.getItem('user');
              localStorage.clear();
              if (token) localStorage.setItem('token', token);
              if (userStr) localStorage.setItem('user', userStr);
              window.location.reload();
            }}
            className="bg-purple-500 hover:bg-purple-600 text-white flex items-center gap-2"
            title="Si ves texto antiguo, presiona aquí"
            data-testid="actualizar-app-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar App
          </Button>
        </div>

        <div className="print-area">
          {/* Print-only report header (visible only when printing) */}
          <div className="hidden print:block mb-4">
            <div className="flex items-center justify-between border-b-2 border-slate-800 pb-2 mb-2">
              <div>
                <h1 className="text-xl font-bold text-slate-900">{classData?.subject || 'Clase'}</h1>
                <p className="text-sm text-slate-600">Grupo: {classData?.group || '—'}</p>
              </div>
              <div className="text-right">
                <p className="text-base font-semibold text-slate-800">
                  {{ students: 'Alumnos', attendance: 'Asistencias', tasks: 'Tareas', civic: 'Participación', eval: 'Evaluación' }[activeTab]}
                </p>
                <p className="text-xs text-slate-500">Impreso: {new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
              </div>
            </div>
          </div>

        <Tabs defaultValue="students" value={activeTab} className="w-full" onValueChange={(v) => { if (user.role === 'teacher') { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); if (hasPending()) flushSaves(); if (civicFlushRef.current) civicFlushRef.current(); if (evalFlushRef.current) evalFlushRef.current(); } setActiveTab(v); if (user.role === 'teacher') { fetchStudents(true); if (v === 'tasks') openTaskConflictIfAny(); if (v === 'civic') setCivicConflictCheck(x => x + 1); } }}>
          <TabsList className="grid w-full grid-cols-5 mb-6">
            <TabsTrigger value="students" data-testid="tab-students" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:font-semibold text-xs">Alumnos</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="tab-attendance" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:font-semibold text-xs">Asistencias</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:font-semibold text-xs">Tareas</TabsTrigger>
            <TabsTrigger value="civic" data-testid="tab-civic" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:font-semibold text-xs">Participación</TabsTrigger>
            <TabsTrigger value="eval" data-testid="tab-eval" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:font-semibold text-xs">Evaluación</TabsTrigger>
          </TabsList>

          {/* Print toolbar (present in every section, prints the active one) */}
          <div className="no-print flex items-center justify-between gap-3 mb-4">
            {user.role === 'teacher'
              ? <LastUpdated timestamp={lastUpdated} />
              : <span />}
            <Button
              onClick={() => window.print()}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-100 flex items-center gap-2"
              data-testid="print-section-btn"
              title="Imprimir esta sección"
            >
              <Printer className="w-4 h-4" />
              Imprimir {{ students: 'Alumnos', attendance: 'Asistencias', tasks: 'Tareas', civic: 'Participación', eval: 'Evaluación' }[activeTab]}
            </Button>
          </div>


          {/* Alumnos Tab */}
          <TabsContent value="students">
            <Card className="p-6">
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-semibold">Lista de Alumnos</h2>
                </div>
                
                {/* Bulk import options - only show if not read-only and bulk import not done */}
                {/* OCULTO: Bloque de importación manual - ya no es necesario, los alumnos se importan automáticamente al crear la clase */}
                {false && !isReadOnly && !classData?.bulk_import_done && (
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-600 mb-3">Importar alumnos (solo se puede usar una opción):</p>
                    <div className="flex gap-3 flex-wrap">
                      {availableGroups && availableGroups.length > 0 && (
                        <Dialog open={importGroupDialog} onOpenChange={setImportGroupDialog}>
                          <DialogTrigger asChild>
                            <Button data-testid="import-group-button" className="bg-purple-600 hover:bg-purple-700 text-white">
                              <Download className="w-4 h-4 mr-2" />
                              Importar desde Grupo
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-white">
                            <DialogHeader>
                              <DialogTitle>Importar Alumnos de un Grupo</DialogTitle>
                            </DialogHeader>
                            <p className="text-sm text-slate-600 mb-4">
                              Selecciona un grupo creado por el administrador para importar sus alumnos a esta clase.
                            </p>
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                              {availableGroups.map(group => (
                                <div key={group.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                                  <div>
                                    <p className="font-medium">{group.name}</p>
                                    <p className="text-sm text-slate-600">{group.student_names?.length || 0} alumnos</p>
                                  </div>
                                  <Button
                                    onClick={() => handleImportGroup(group.id)}
                                    className="bg-purple-500 hover:bg-purple-600"
                                    data-testid={`import-group-${group.id}`}
                                  >
                                    Importar
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                      
                      {/* OCULTO: Botón "Copiar de Otra Clase" - descomentarear para reactivar */}
                      {false && allClasses && allClasses.filter(c => c.id !== classId && c.student_ids?.length > 0).length > 0 && (
                        <Dialog open={copyStudentsDialog} onOpenChange={setCopyStudentsDialog}>
                          <DialogTrigger asChild>
                            <Button data-testid="copy-students-button" className="bg-slate-600 hover:bg-slate-700 text-white">
                              <Copy className="w-4 h-4 mr-2" />
                              Copiar de Otra Clase
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-white">
                            <DialogHeader>
                              <DialogTitle>Copiar Alumnos de Otra Clase</DialogTitle>
                            </DialogHeader>
                            <p className="text-sm text-slate-600 mb-4">
                              Selecciona una clase para copiar sus alumnos a esta clase.
                            </p>
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                              {allClasses.filter(c => c.id !== classId && c.student_ids?.length > 0).map(classItem => (
                                <div key={classItem.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                                  <div>
                                    <p className="font-medium">{classItem.subject}</p>
                                    <p className="text-sm text-slate-600">{classItem.group} • {classItem.student_ids?.length || 0} alumnos</p>
                                    <p className="text-xs text-slate-500">Prof: {classItem.teacher_name}</p>
                                  </div>
                                  <Button
                                    onClick={() => handleCopyStudents(classItem.id)}
                                    className="bg-slate-600 hover:bg-slate-700"
                                    data-testid={`copy-from-${classItem.id}`}
                                  >
                                    Copiar
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Show message if bulk import was done */}
                {!isReadOnly && classData?.bulk_import_done && (
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800">
                      La gestión de alumnos se realiza desde Control Escolar.
                    </p>
                  </div>
                )}
                
                {/* Note: Manual student management moved to Control Escolar */}
              </div>

              <div className="space-y-2">
                {students.length === 0 ? (
                  <p className="text-center text-slate-600 py-12">No hay alumnos en esta clase.</p>
                ) : (
                  <div className="space-y-1">
                    {/* Notification action bar */}
                    {!isReadOnly && (
                      <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3 mb-3 border border-slate-200">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedStudentsForNotif.length === filteredStudents.length && filteredStudents.length > 0}
                            onCheckedChange={toggleAllStudentsNotif}
                            data-testid="select-all-students-notif"
                          />
                          <span className="text-sm text-slate-600">
                            {selectedStudentsForNotif.length > 0 
                              ? `${selectedStudentsForNotif.length} alumno${selectedStudentsForNotif.length !== 1 ? 's' : ''} seleccionado${selectedStudentsForNotif.length !== 1 ? 's' : ''}`
                              : 'Seleccionar alumnos para notificar'
                            }
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => openTeacherNotifForm('citatorio')}
                            disabled={selectedStudentsForNotif.length === 0}
                            className="bg-red-500 hover:bg-red-600 text-white text-xs h-8 px-3"
                            data-testid="teacher-citatorio-btn"
                          >
                            <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                            Citatorio
                          </Button>
                          <Button
                            onClick={() => openTeacherNotifForm('aviso')}
                            disabled={selectedStudentsForNotif.length === 0}
                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs h-8 px-3"
                            data-testid="teacher-aviso-btn"
                          >
                            <Bell className="w-3.5 h-3.5 mr-1" />
                            Aviso
                          </Button>
                        </div>
                      </div>
                    )}
                    {students.map((student, index) => (
                      <div key={student.id} className={`flex items-center justify-between py-3 px-4 border rounded-lg transition-colors ${
                        selectedStudentsForNotif.includes(student.name) ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'
                      }`}>
                        <div className="flex items-center gap-3">
                          {!isReadOnly && (
                            <Checkbox
                              checked={selectedStudentsForNotif.includes(student.name)}
                              onCheckedChange={() => toggleStudentNotifSelection(student.name)}
                              data-testid={`select-student-${student.id}`}
                            />
                          )}
                          <span className="text-sm font-medium text-slate-900 w-12">{index + 1}.</span>
                          <span className="text-sm text-slate-900">
                            {student.name}
                            {student.matricula && <span className="block text-xs text-slate-400 font-normal">Mat: {student.matricula}</span>}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Edit Student Dialog */}
              <Dialog open={editStudentDialog} onOpenChange={setEditStudentDialog}>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle>Editar Alumno</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpdateStudent} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-student-name">Nombre Completo (Apellidos, Nombre)</Label>
                      <Input
                        id="edit-student-name"
                        data-testid="edit-student-name-input"
                        value={editStudentName}
                        onChange={(e) => setEditStudentName(e.target.value)}
                        required
                        placeholder="Ej: García Pérez, Juan Carlos"
                      />
                    </div>
                    <Button type="submit" data-testid="update-student-button" className="w-full bg-orange-500 hover:bg-orange-600">
                      Actualizar
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Teacher Citatorio Dialog */}
              <Dialog open={teacherNotifType === 'citatorio'} onOpenChange={() => closeTeacherNotifForm()}>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Enviar Citatorio
                    </DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-slate-600 mb-2">
                    Se enviará a: <span className="font-medium">{selectedStudentsForNotif.join(', ')}</span>
                  </p>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Fecha</Label>
                        <Input type="date" value={teacherCitatorioForm.fecha}
                          onChange={(e) => setTeacherCitatorioForm({...teacherCitatorioForm, fecha: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Hora</Label>
                        <Input type="time" value={teacherCitatorioForm.hora}
                          onChange={(e) => setTeacherCitatorioForm({...teacherCitatorioForm, hora: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Lugar</Label>
                      <Input value={teacherCitatorioForm.lugar}
                        onChange={(e) => setTeacherCitatorioForm({...teacherCitatorioForm, lugar: e.target.value})}
                        placeholder="Ej: Salón 101"
                      />
                    </div>
                    <div>
                      <Label>Motivo</Label>
                      <Input className="w-full" rows={3}
                        value={teacherCitatorioForm.motivo}
                        onChange={(e) => setTeacherCitatorioForm({...teacherCitatorioForm, motivo: e.target.value})}
                        placeholder="Motivo del citatorio..."
                      />
                    </div>
                    <Button onClick={sendTeacherCitatorio} disabled={sendingTeacherNotif}
                      className="w-full bg-red-500 hover:bg-red-600 text-white"
                      data-testid="send-teacher-citatorio"
                    >
                      {sendingTeacherNotif ? 'Enviando...' : 'Enviar Citatorio'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Teacher Aviso Dialog */}
              <Dialog open={teacherNotifType === 'aviso'} onOpenChange={() => closeTeacherNotifForm()}>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Bell className="w-5 h-5 text-blue-500" />
                      Enviar Aviso
                    </DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-slate-600 mb-2">
                    Se enviará a: <span className="font-medium">{selectedStudentsForNotif.join(', ')}</span>
                  </p>
                  <div className="space-y-3">
                    <div>
                      <Label>Mensaje</Label>
                      <Input className="w-full" rows={4}
                        value={teacherAvisoForm.mensaje}
                        onChange={(e) => setTeacherAvisoForm({...teacherAvisoForm, mensaje: e.target.value})}
                        placeholder="Escribe el aviso..."
                      />
                    </div>
                    <Button onClick={sendTeacherAviso} disabled={sendingTeacherNotif}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                      data-testid="send-teacher-aviso"
                    >
                      {sendingTeacherNotif ? 'Enviando...' : 'Enviar Aviso'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Sent Notifications Section */}
              {!isReadOnly && (
                <div className="mt-6 border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => { setShowSentNotifications(!showSentNotifications); if (!showSentNotifications) fetchSentNotifications(); }}
                    className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                    data-testid="toggle-sent-notifs"
                  >
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">Notificaciones Enviadas</span>
                      {sentNotifications.length > 0 && (
                        <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{sentNotifications.length}</span>
                      )}
                    </div>
                    {showSentNotifications ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </button>
                  {showSentNotifications && (
                    <div className="p-3 space-y-2 max-h-[300px] overflow-y-auto">
                      {sentNotifications.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">No hay notificaciones enviadas</p>
                      ) : (
                        sentNotifications.map((notif) => (
                          <div key={notif.id} className={`p-3 rounded-lg border text-sm ${notif.notification_type === 'citatorio' ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'}`} data-testid={`sent-notif-${notif.id}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                {notif.notification_type === 'citatorio' ? (
                                  <FileText className="w-4 h-4 text-rose-600" />
                                ) : (
                                  <Megaphone className="w-4 h-4 text-amber-600" />
                                )}
                                <span className={`font-bold uppercase text-xs ${notif.notification_type === 'citatorio' ? 'text-rose-700' : 'text-amber-700'}`}>
                                  {notif.notification_type}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-400">
                                  {new Date(notif.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <button
                                  onClick={() => deleteTeacherNotification(notif.id)}
                                  className="text-red-400 hover:text-red-600 transition-colors p-0.5"
                                  title="Eliminar notificación"
                                  data-testid={`delete-teacher-notif-${notif.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                            {notif.notification_type === 'citatorio' ? (
                              <div className="mt-1 text-xs text-slate-600 flex items-center gap-3 flex-wrap">
                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{notif.fecha}</span>
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{notif.hora}</span>
                                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{notif.lugar}</span>
                              </div>
                            ) : (
                              <p className="mt-1 text-xs text-slate-600 line-clamp-2">{notif.mensaje}</p>
                            )}
                            {notif.student_names && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {notif.student_names.map((name, i) => (
                                  <span key={i} className="text-xs bg-white px-1.5 py-0.5 rounded border border-slate-200 text-slate-500">{name}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Asistencias Tab */}
          <TabsContent value="attendance">
            {user.role === 'teacher' && (
              <PartialReminderBanner
                partialDates={partialDates}
                separatorCount={(attendanceGrid.columns || []).filter(c => c.isSeparator).length}
                sectionLabel="Asistencias"
              />
            )}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-semibold">Registro de Asistencias</h2>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded">1 = Asistencia</span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded">0 = Falta</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded">J = Justificada</span>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">R = Retardo</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">JO = Justificación Orientador</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs flex-wrap mt-1">
                    <span className="text-slate-500 font-medium">Alertas:</span>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">Nombre amarillo = 10%+ inasistencias</span>
                    <span className="px-2 py-1 bg-red-100 text-red-600 rounded">Nombre rojo = 20%+ inasistencias</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative no-print">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar alumno..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-64"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        <X className="w-4 h-4 text-slate-400" />
                      </button>
                    )}
                  </div>
                  {!isReadOnly && (
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2 flex-nowrap">
                        <Button
                          onClick={handleAddToday}
                          data-testid="add-today-button"
                          className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
                          title="Agrega una columna con la fecha de hoy"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          HOY
                        </Button>
                        <Dialog open={addDateDialog} onOpenChange={setAddDateDialog}>
                          <DialogTrigger asChild>
                            <Button data-testid="add-date-button" className="bg-orange-500 hover:bg-orange-600 whitespace-nowrap">
                              <Plus className="w-4 h-4 mr-2" />
                              Agregar Fecha
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-white">
                            <DialogHeader>
                              <DialogTitle>Agregar Nueva Fecha</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleAddDate} className="space-y-4">
                              <div className="space-y-2">
                                <Label htmlFor="new-date">Fecha</Label>
                                <Input
                                  id="new-date"
                                  type="date"
                                  value={newDate}
                                  onChange={(e) => setNewDate(e.target.value)}
                                  required
                                />
                              </div>
                              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600">
                                Agregar
                              </Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                        <Button
                          onClick={handleAsistenciaGeneral}
                          data-testid="asistencia-general-button"
                          className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                          title="Marca asistencia (1) a todos hoy, excepto los que tengan JO"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Asistencia General
                        </Button>
                        <Button
                          onClick={handleFaltaGeneral}
                          data-testid="falta-general-button"
                          className="bg-red-600 hover:bg-red-700 whitespace-nowrap"
                          title="Marca falta (0) a todos hoy, excepto los que tengan JO"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Falta General
                        </Button>
                      </div>
                      <div className="flex justify-center gap-2 flex-wrap">
                        <Button
                          onClick={() => { setSuspensionForm({ date: '', motivo: '' }); setSuspensionDialog(true); }}
                          data-testid="suspension-clase-button"
                          className="bg-slate-700 hover:bg-slate-800 whitespace-nowrap"
                          title="Agrega una columna informativa de día suspendido (no afecta el %)"
                        >
                          <span className="mr-2 font-bold text-red-400">✕</span>
                          Suspensión de Clase
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            const existingAttSeps = attendanceGrid.columns.filter(c => c.isSeparator).length;
                            if (existingAttSeps >= 3) {
                              toast.error('Máximo 3 separadores de parcial permitidos');
                              return;
                            }
                            const separatorId = `separator_${Date.now()}`;
                            const names = ['FIN DE PRIMER PARCIAL', 'FIN DEL SEGUNDO PARCIAL', 'FIN DEL TERCER PARCIAL'];
                            const label = names[existingAttSeps];
                            const newColumns = [...attendanceGrid.columns, { id: separatorId, isSeparator: true, label, date: null }];
                            const token = localStorage.getItem('token');
                            markSaving();
                            axios.put(`${API}/attendance-grid/${classId}`, { columns: newColumns, records: attendanceGrid.records }, { headers: { Authorization: `Bearer ${token}` } })
                              .then(() => axios.get(`${API}/attendance-grid/${classId}`, { headers: { Authorization: `Bearer ${token}` } }))
                              .then(res => { setAttendanceGrid(res.data); markSaved(); toast.success(`Separador "${label}" agregado`); })
                              .catch(() => { markSaveError(); toast.error('Error al agregar separador'); });
                          }}
                          className="border-purple-300 text-purple-600 hover:bg-purple-50"
                          data-testid="add-separator-attendance"
                        >
                          <span className="w-1 h-4 bg-purple-400 rounded mr-2"></span>
                          Separador de Parcial
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4 md:hidden bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
                <span className="text-sm text-blue-700 font-medium">Desliza horizontalmente para ver todas las columnas →</span>
              </div>

              <Dialog open={suspensionDialog} onOpenChange={setSuspensionDialog}>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle>Suspensión de Clase</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddSuspension} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="susp-date">Fecha de la suspensión</Label>
                      <Input
                        id="susp-date"
                        type="date"
                        value={suspensionForm.date}
                        onChange={(e) => setSuspensionForm({ ...suspensionForm, date: e.target.value })}
                        data-testid="suspension-date-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="susp-motivo">Motivo</Label>
                      <textarea
                        id="susp-motivo"
                        value={suspensionForm.motivo}
                        onChange={(e) => setSuspensionForm({ ...suspensionForm, motivo: e.target.value })}
                        placeholder="Ej. Consejo Técnico Escolar, día festivo, etc."
                        rows={3}
                        className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400 resize-none"
                        data-testid="suspension-motivo-input"
                        required
                      />
                    </div>
                    <p className="text-xs text-slate-500">Esta columna es informativa: no afecta el porcentaje de asistencias. Todas las casillas mostrarán una ✕ roja.</p>
                    <Button type="submit" className="w-full bg-slate-700 hover:bg-slate-800" data-testid="suspension-submit-btn">
                      Registrar suspensión
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <div className="relative">
                <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {(() => {
                    const allAttCols = buildAttColumnsWithCumulative(attendanceGrid.columns);
                    const displayAttCols = allAttCols.filter(c => !attSepState.hiddenColIds.has(c.id));
                    return (
                  <table className="w-full border-collapse min-w-max">
                      <thead className="grid-sticky-header sticky top-0 z-20">
                        <tr className="border-b-2 border-slate-300 bg-slate-50">
                          <th className="sticky left-0 z-30 bg-slate-50 text-left py-2 px-3 text-sm font-semibold text-slate-700 border-r-2 border-slate-300 min-w-[180px] max-w-[180px] shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                            Alumno
                          </th>
                          {displayAttCols.map((col, idx) => (
                            col._isAttCumulative ? (
                              <th key={`acum-${idx}`} className="text-center py-2 px-1 text-xs font-bold text-blue-700 border-r border-slate-200 min-w-[60px] whitespace-nowrap bg-blue-50">
                                <div className="flex flex-col items-center gap-0.5">
                                  <div className="font-bold text-blue-600">{col.label}</div>
                                  <div className="text-[10px] text-blue-400">Asist.</div>
                                </div>
                              </th>
                            ) : col.isSuspension ? (
                              <th key={idx}
                                title={`Clase suspendida — ${col.motivo || 'sin motivo'}`}
                                className="text-center py-2 px-1 text-sm font-semibold text-slate-700 border-r border-slate-200 min-w-[64px] max-w-[90px] whitespace-nowrap bg-red-50"
                                data-testid={`susp-col-header-${col.id}`}
                              >
                                <div className="flex flex-col items-center gap-1">
                                  <span className="text-xs">{col.label}</span>
                                  <span className="text-[8px] font-bold text-red-500 leading-none uppercase">Susp.</span>
                                  {col.motivo && (
                                    <span
                                      className="block max-w-[82px] truncate text-[8px] font-normal normal-case text-red-600 leading-tight"
                                      title={col.motivo}
                                      data-testid={`susp-col-motivo-${col.id}`}
                                    >
                                      {col.motivo}
                                    </span>
                                  )}
                                  {!isReadOnly && (
                                    <button
                                      onClick={() => handleDeleteDateColumn(col.id)}
                                      className="text-red-500 hover:text-red-700 text-xs"
                                      title="Eliminar suspensión"
                                      data-testid={`delete-susp-${col.id}`}
                                    >
                                      &#10005;
                                    </button>
                                  )}
                                </div>
                              </th>
                            ) : col.isSeparator ? (
                              <th key={idx}
                                onClick={() => attSepState.toggle(col.id)}
                                title={attSepState.isCollapsed(col.id) ? `Mostrar columnas del parcial ${col.label}` : `Ocultar columnas del parcial ${col.label}`}
                                className={`text-center py-1 px-0 text-xs font-bold text-purple-700 border-r border-slate-200 ${attSepState.isCollapsed(col.id) ? 'min-w-[40px] max-w-[40px] bg-purple-200' : 'min-w-[28px] max-w-[28px] bg-gradient-to-b from-purple-100 to-purple-200'} whitespace-nowrap cursor-pointer hover:bg-purple-300`}
                                style={attSepState.isCollapsed(col.id) ? {} : { backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(147, 51, 234, 0.15) 2px, rgba(147, 51, 234, 0.15) 4px)' }}>
                                <div className="flex flex-col items-center gap-0">
                                  <span className="text-purple-700 text-[8px] font-bold truncate w-full">{col.label}</span>
                                  {attSepState.isCollapsed(col.id) ? (
                                    <span className="text-purple-700 text-[9px] font-bold mt-0.5" title="Clic para mostrar">▶</span>
                                  ) : (
                                    !isReadOnly && (
                                      <div className="flex flex-col items-center gap-0 text-[10px]">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleMoveSeparator(col.id, 'left', 'attendance'); }}
                                          className="text-purple-400 hover:text-purple-700 leading-none"
                                          title="Mover izquierda"
                                          disabled={idx === 0}
                                        >&#9664;</button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleDeleteDateColumn(col.id); }}
                                          className="text-purple-400 hover:text-red-600 leading-none"
                                          title="Eliminar"
                                        >&#10005;</button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleMoveSeparator(col.id, 'right', 'attendance'); }}
                                          className="text-purple-400 hover:text-purple-700 leading-none"
                                          title="Mover derecha"
                                          disabled={idx === displayAttCols.length - 1}
                                        >&#9654;</button>
                                      </div>
                                    )
                                  )}
                                </div>
                              </th>
                            ) : (
                              <th key={idx} className="text-center py-2 px-1 text-sm font-semibold text-slate-700 border-r border-slate-200 min-w-[50px] whitespace-nowrap bg-slate-50">
                                <div className="flex flex-col items-center gap-1">
                                  <span className="text-xs">{col.label}</span>
                                  {!isReadOnly && (
                                    <button
                                      onClick={() => handleDeleteDateColumn(col.id)}
                                      className="text-red-500 hover:text-red-700 text-xs"
                                      title="Eliminar fecha"
                                      data-testid={`delete-date-${col.id}`}
                                    >
                                      &#10005;
                                    </button>
                                  )}
                                </div>
                              </th>
                            )
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {filteredStudents.length === 0 ? (
                          <tr>
                            <td colSpan={displayAttCols.length + 1} className="text-center py-12 text-slate-600">
                              No hay alumnos
                            </td>
                          </tr>
                        ) : (
                          filteredStudents.map((student) => {
                            // Calculate absence percentage based on REGISTERED attendance only
                            const dataColumns = attendanceGrid.columns.filter(c => !c.isSeparator && !c.isSuspension);
                            const studentRecord = attendanceGrid.records[student.id] || {};
                            let registered = 0;
                            let absences = 0;
                            dataColumns.forEach((col) => {
                              const colId = col.id || col.date;
                              const val = (studentRecord[colId] || '').toUpperCase();
                              const isJO = hasOrientadorJustification(student.id, col);
                              if (isJO) {
                                registered++;
                              } else if (val !== '') {
                                registered++;
                                if (val === '0') absences++;
                              }
                            });
                            const absencePct = registered > 0 ? (absences / registered) * 100 : 0;
                            const nameColor = absencePct >= 20 ? 'bg-red-100' : absencePct >= 10 ? 'bg-yellow-100' : 'bg-white';

                            return (
                            <tr key={student.id} className="border-b border-slate-200 hover:bg-slate-50">
                              <td className={`sticky left-0 z-10 ${nameColor} py-2 px-3 text-sm font-medium text-slate-900 border-r-2 border-slate-300 min-w-[180px] max-w-[180px] shadow-[2px_0_4px_rgba(0,0,0,0.1)]`}>
                                <div className="whitespace-normal leading-tight">
                                  {student.name}
                                  {student.matricula && <span className="block text-[10px] text-slate-400 font-normal">Mat: {student.matricula}</span>}
                                  {registered > 0 && (
                                    <span className={`ml-1 text-xs font-bold ${absencePct >= 20 ? 'text-red-600' : absencePct >= 10 ? 'text-yellow-600' : 'text-slate-500'}`}>
                                      ({Math.round(absencePct)}% de inasistencias, de un total de {dataColumns.length} clases)
                                    </span>
                                  )}
                                </div>
                              </td>
                              {displayAttCols.map((col, idx) => {
                                if (col._isAttCumulative) {
                                  const att = calcAttCounts(studentRecord, col._colIds, hasOrientadorJustification, student.id, attendanceGrid.columns);
                                  const attPct = att.pct;
                                  const color = attPct === null ? 'bg-blue-50 text-slate-400' : attPct >= 80 ? 'bg-green-100 text-green-700' : attPct >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
                                  return (
                                    <td key={`acum-${idx}`} className={`text-center py-1 px-1 border-r border-slate-200 ${color}`}>
                                      <div className="w-16 h-10 mx-auto flex flex-col items-center justify-center leading-tight rounded-lg" title={attPct !== null ? `${att.present} de ${att.registered} asistencias registradas` : 'Sin asistencias registradas'}>
                                        <span className="text-sm font-bold">{attPct !== null ? `${attPct}%` : '-'}</span>
                                        {attPct !== null && <span className="text-[9px] font-semibold opacity-80">{att.present}/{att.registered}</span>}
                                      </div>
                                    </td>
                                  );
                                }
                                if (col.isSuspension) {
                                  return (
                                    <td key={idx} className="text-center py-1 px-1 border-r border-slate-200 bg-red-50"
                                        title={`Clase suspendida — ${col.motivo || 'sin motivo'}`}
                                        data-testid={`susp-cell-${col.id}`}>
                                      <span className="text-red-600 font-bold text-lg select-none">✕</span>
                                    </td>
                                  );
                                }
                                if (col.isSeparator) {
                                  const isCollapsedSep = attSepState.isCollapsed(col.id);
                                  return (
                                    <td key={idx} className={`text-center py-1 px-0 border-r border-slate-200 ${isCollapsedSep ? 'min-w-[40px] max-w-[40px]' : 'min-w-[28px] max-w-[28px]'}`} style={isCollapsedSep ? { backgroundColor: 'rgb(216, 180, 254)' } : { backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(147, 51, 234, 0.15) 2px, rgba(147, 51, 234, 0.15) 4px)', backgroundColor: 'rgb(243, 232, 255)' }}>
                                    </td>
                                  );
                                }
                                
                                const hasJO = hasOrientadorJustification(student.id, col);
                                const colId = col.id || col.date;
                                const value = attendanceGrid.records[student.id]?.[colId] || '';
                                return (
                                  <td key={idx} className="text-center py-1 px-1 border-r border-slate-200 bg-white">
                                    <AttendanceCell
                                      studentId={student.id}
                                      colId={colId}
                                      value={value}
                                      isReadOnly={isReadOnly}
                                      isJO={hasJO}
                                      onChange={handleAttendanceChange}
                                      hasNote={!!attendanceGrid.notes?.[student.id]?.[colId]}
                                      noteText={attendanceGrid.notes?.[student.id]?.[colId] || ''}
                                      onOpenNote={openAttNoteDialog}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                    );
                  })()}
                </div>
              </div>
              <AttendanceGradesSection
                attWeights={attWeights}
                onWeightChange={handleAttWeightChange}
                isReadOnly={isReadOnly}
                students={filteredStudents}
                attendanceByParcial={attendanceByParcial}
              />
            </Card>
          </TabsContent>

          {/* Tareas Tab */}
          <TabsContent value="tasks">
            {user.role === 'teacher' && (
              <PartialReminderBanner
                partialDates={partialDates}
                separatorCount={(taskGrid.columns || []).filter(c => c.isSeparator).length}
                sectionLabel="Tareas"
              />
            )}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-semibold">Registro de Tareas</h2>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded">80-100% Excelente</span>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">60-79% Regular</span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded">0-59% Bajo</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative no-print">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar alumno..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-64"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        <X className="w-4 h-4 text-slate-400" />
                      </button>
                    )}
                  </div>
                  {!isReadOnly && (
                    <div className="flex gap-2">
                      <Dialog open={addTaskDialog} onOpenChange={setAddTaskDialog}>
                        <DialogTrigger asChild>
                          <Button data-testid="add-task-button" className="bg-orange-500 hover:bg-orange-600">
                            <Plus className="w-4 h-4 mr-2" />
                            Agregar Tarea
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white">
                          <DialogHeader>
                            <DialogTitle>Agregar Nueva Tarea</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleAddTask} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="task-date">Fecha</Label>
                              <Input
                                id="task-date"
                                type="date"
                                value={newTask.date}
                                onChange={(e) => setNewTask({ ...newTask, date: e.target.value })}
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="task-description">Nombre de la tarea</Label>
                              <Input
                                id="task-description"
                                value={newTask.description}
                                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                                placeholder="Ej: Ejercicios cap. 3"
                              />
                            </div>
                            <p className="text-xs text-slate-500">Se asignará automáticamente: {getNextTaskNumber(taskGrid.columns)}</p>
                            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600">
                              Agregar
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>

                      {/* Edit Task Dialog */}
                      <Dialog open={editTaskDialog} onOpenChange={(v) => { setEditTaskDialog(v); if (!v) setEditingTaskCol(null); }}>
                        <DialogContent className="bg-white">
                          <DialogHeader>
                            <DialogTitle>Editar Tarea</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleEditTask} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="edit-task-date">Fecha</Label>
                              <Input
                                id="edit-task-date"
                                type="date"
                                value={editTaskForm.date}
                                onChange={(e) => setEditTaskForm({ ...editTaskForm, date: e.target.value })}
                                required
                                noDebounce
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="edit-task-desc">Nombre de la tarea</Label>
                              <Input
                                id="edit-task-desc"
                                value={editTaskForm.description}
                                onChange={(e) => setEditTaskForm({ ...editTaskForm, description: e.target.value })}
                                placeholder="Ej: Ejercicios cap. 3"
                                noDebounce
                              />
                            </div>
                            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600">
                              Guardar Cambios
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>

                      <Button
                        variant="outline"
                        onClick={() => {
                          const existingTaskSeps = taskGrid.columns.filter(c => c.isSeparator).length;
                          if (existingTaskSeps >= 3) {
                            toast.error('Máximo 3 separadores de parcial permitidos');
                            return;
                          }
                          // Auto-add task separator without dialog
                          const separatorId = `separator_${Date.now()}`;
                          const names = ['FIN DE PRIMER PARCIAL', 'FIN DEL SEGUNDO PARCIAL', 'FIN DEL TERCER PARCIAL'];
                          const label = names[existingTaskSeps];
                          const newColumns = [...taskGrid.columns, { id: separatorId, isSeparator: true, label, date: null }];
                          const token = localStorage.getItem('token');
                          markSaving();
                          axios.put(`${API}/task-grid/${classId}`, { columns: newColumns, records: taskGrid.records }, { headers: { Authorization: `Bearer ${token}` } })
                            .then(() => axios.get(`${API}/task-grid/${classId}`, { headers: { Authorization: `Bearer ${token}` } }))
                            .then(res => { setTaskGrid(res.data); markSaved(); toast.success(`Separador "${label}" agregado`); })
                            .catch(() => { markSaveError(); toast.error('Error al agregar separador'); });
                        }}
                        className="border-purple-300 text-purple-600 hover:bg-purple-50"
                        data-testid="add-separator-task"
                      >
                        <span className="w-1 h-4 bg-purple-400 rounded mr-2"></span>
                        Separador de Parcial
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4 md:hidden bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
                <span className="text-sm text-blue-700 font-medium">Desliza horizontalmente para ver todas las tareas →</span>
              </div>

              <div className="relative">
                <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {(() => {
                    const allTaskCols = buildTaskColumnsWithCumulative(taskGrid.columns);
                    const displayColumns = allTaskCols.filter(c => !taskSepState.hiddenColIds.has(c.id));
                    return (
                  <table className="w-full border-collapse min-w-max">
                      <thead className="grid-sticky-header sticky top-0 z-20">
                        <tr className="border-b-2 border-slate-300 bg-slate-50">
                          <th className="sticky left-0 z-30 bg-slate-50 text-left py-2 px-3 text-sm font-semibold text-slate-700 border-r-2 border-slate-300 min-w-[180px] max-w-[180px] shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                            Alumno
                          </th>
                          {displayColumns.map((col, idx) => (
                            col._isCumulative ? (
                              <th key={`cum-${idx}`} className="text-center py-2 px-1 text-xs font-bold text-blue-700 border-r border-slate-200 min-w-[60px] whitespace-nowrap bg-blue-50">
                                <div className="flex flex-col items-center gap-0.5">
                                  <div className="font-bold text-blue-600">{col.label}</div>
                                  <div className="text-[10px] text-blue-400">Cumpl.</div>
                                </div>
                              </th>
                            ) : col.isSeparator ? (
                              <th key={idx}
                                onClick={() => taskSepState.toggle(col.id)}
                                title={taskSepState.isCollapsed(col.id) ? `Mostrar columnas del parcial ${col.label}` : `Ocultar columnas del parcial ${col.label}`}
                                className={`text-center py-1 px-0 text-xs font-bold text-purple-700 border-r border-slate-200 ${taskSepState.isCollapsed(col.id) ? 'min-w-[40px] max-w-[40px] bg-purple-200' : 'min-w-[28px] max-w-[28px] bg-gradient-to-b from-purple-100 to-purple-200'} whitespace-nowrap cursor-pointer hover:bg-purple-300`}
                                style={taskSepState.isCollapsed(col.id) ? {} : { backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(147, 51, 234, 0.15) 2px, rgba(147, 51, 234, 0.15) 4px)' }}>
                                <div className="flex flex-col items-center gap-0">
                                  <span className="text-purple-700 text-[8px] font-bold truncate w-full">{col.label}</span>
                                  {taskSepState.isCollapsed(col.id) ? (
                                    <span className="text-purple-700 text-[9px] font-bold mt-0.5" title="Clic para mostrar">▶</span>
                                  ) : (
                                    !isReadOnly && (
                                      <div className="flex flex-col items-center gap-0 text-[10px]">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleMoveSeparator(col.id, 'left', 'task'); }}
                                          className="text-purple-400 hover:text-purple-700 leading-none"
                                          title="Mover izquierda"
                                          disabled={idx === 0}
                                        >&#9664;</button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleDeleteTaskColumn(col.id); }}
                                          className="text-purple-400 hover:text-red-600 leading-none"
                                          title="Eliminar"
                                        >&#10005;</button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleMoveSeparator(col.id, 'right', 'task'); }}
                                          className="text-purple-400 hover:text-purple-700 leading-none"
                                          title="Mover derecha"
                                          disabled={idx === displayColumns.length - 1}
                                        >&#9654;</button>
                                      </div>
                                    )
                                  )}
                                </div>
                              </th>
                            ) : (
                              (() => {
                                // Find original index in taskGrid.columns to compute dynamic number
                                const origIdx = taskGrid.columns.findIndex(c => c.id === col.id);
                                const dynNumber = origIdx >= 0 ? getTaskDisplayNumber(taskGrid.columns, origIdx) : (col.number || '');
                                const fullName = col.description ? `${dynNumber} - ${col.description}` : dynNumber;
                                return (
                              <th key={idx} className="text-center py-2 px-1 text-xs font-semibold text-slate-700 border-r border-slate-200 min-w-[60px] max-w-[60px] whitespace-nowrap bg-slate-50 overflow-hidden" title={fullName} data-testid={`task-header-${col.id}`}>
                                <div className="flex flex-col items-center gap-0.5">
                                  <div className="font-bold">{dynNumber}</div>
                                  <div className="font-normal text-slate-500">{col.date ? col.date.split('-').reverse().slice(0,2).join('/') : ''}</div>
                                  {col.description && (
                                    <div
                                      className="font-normal text-slate-500 truncate max-w-[55px] cursor-pointer"
                                      title={col.description}
                                      onClick={() => setTaskNamePopup(fullName)}
                                    >{col.description}</div>
                                  )}
                                  {!isReadOnly && (
                                    <div className="flex items-center gap-1 mt-0.5">
                                      <button
                                        onClick={() => openEditTask(col)}
                                        className="text-blue-500 hover:text-blue-700"
                                        title="Editar tarea"
                                        data-testid={`edit-task-${col.id}`}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                                      </button>
                                      <button
                                        onClick={() => handleDeleteTaskColumn(col.id)}
                                        className="text-red-500 hover:text-red-700 text-xs"
                                        title="Eliminar tarea"
                                        data-testid={`delete-task-${col.id}`}
                                      >
                                        &#10005;
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </th>
                                );
                              })()
                            )
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {filteredStudents.length === 0 ? (
                          <tr>
                            <td colSpan={displayColumns.length + 1} className="text-center py-12 text-slate-600">
                              No hay alumnos
                            </td>
                          </tr>
                        ) : (
                          filteredStudents.map((student) => {
                            const studentRecords = taskGrid.records[student.id] || {};
                            return (
                            <tr key={student.id} className="border-b border-slate-200 hover:bg-slate-50">
                              <td className="sticky left-0 z-10 bg-white py-2 px-3 text-sm font-medium text-slate-900 border-r-2 border-slate-300 min-w-[180px] max-w-[180px] shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                <div className="whitespace-normal leading-tight">
                                  {student.name}
                                  {student.matricula && <span className="block text-[10px] text-slate-400 font-normal">Mat: {student.matricula}</span>}
                                </div>
                              </td>
                              {displayColumns.map((col, idx) => {
                                if (col._isCumulative) {
                                  const avg = calcCumulative(studentRecords, col._taskIds);
                                  const color = avg === null ? 'bg-blue-50 text-slate-400' : avg >= 80 ? 'bg-green-100 text-green-700' : avg >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
                                  return (
                                    <td key={`cum-${idx}`} className={`text-center py-1 px-1 border-r border-slate-200 ${color}`}>
                                      <div className="w-16 h-10 mx-auto flex items-center justify-center text-sm font-bold rounded-lg">
                                        {avg !== null ? `${avg}%` : '-'}
                                      </div>
                                    </td>
                                  );
                                }
                                if (col.isSeparator) {
                                  const isTaskCol = taskSepState.isCollapsed(col.id);
                                  return (
                                    <td key={idx} className={`text-center py-1 px-0 border-r border-slate-200 ${isTaskCol ? 'min-w-[40px] max-w-[40px]' : 'min-w-[28px] max-w-[28px]'}`} style={isTaskCol ? { backgroundColor: 'rgb(216, 180, 254)' } : { backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(147, 51, 234, 0.15) 2px, rgba(147, 51, 234, 0.15) 4px)', backgroundColor: 'rgb(243, 232, 255)' }}>
                                    </td>
                                  );
                                }
                                
                                const value = studentRecords[col.id] || '';
                                const taskHasJO = hasOrientadorJustification(student.id, col);
                                return (
                                  <td key={idx} className="text-center py-1 px-1 border-r border-slate-200 bg-white">
                                    <div className="relative inline-block">
                                      {taskHasJO && <span className="absolute -top-1.5 -right-1 text-blue-600 text-base font-black leading-none z-10" title="Justificación del Orientador">*</span>}
                                      <TaskCell
                                        studentId={student.id}
                                        colId={col.id}
                                        value={value}
                                        isReadOnly={isReadOnly}
                                        onChange={handleTaskChange}
                                        hasNote={!!(taskNotes[student.id] && taskNotes[student.id][col.id])}
                                        noteText={(taskNotes[student.id] && taskNotes[student.id][col.id]) || ''}
                                        onOpenNote={openNoteDialog}
                                      />
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                    );
                  })()}
                </div>
              </div>

              {/* Calificación por Tareas */}
              {(() => {
                const cols = taskGrid.columns;
                const hasSeparators = cols.some(c => c.isSeparator);
                const blocks = [];
                let currentBlock = [];
                let pIdx = 0;
                for (const col of cols) {
                  if (col.isSeparator) {
                    pIdx++;
                    blocks.push({ parcial: pIdx, taskIds: currentBlock.map(c => c.id), label: `Parcial ${pIdx}` });
                    currentBlock = [];
                  } else {
                    currentBlock.push(col);
                  }
                }
                if (currentBlock.length > 0) {
                  pIdx++;
                  blocks.push({ parcial: pIdx, taskIds: currentBlock.map(c => c.id), label: hasSeparators ? `Parcial ${pIdx}` : 'General' });
                }

                const parcialKeys = ['parcial_1', 'parcial_2', 'parcial_3'];
                const hasAnyWeight = parcialKeys.some(k => taskWeights[k] && taskWeights[k] !== '');

                return (
                  <div className="mt-6 border-t-2 border-indigo-100 pt-4" data-testid="task-grades-section">
                    <h3 className="text-lg font-semibold text-indigo-700 mb-3">Calificación por Tareas (sobre 10)</h3>

                    <div className="flex flex-wrap gap-4 mb-4">
                      {[1, 2, 3].map(p => (
                        <div key={p} className="flex items-center gap-2 bg-indigo-50 rounded-lg px-3 py-2">
                          <span className="text-sm font-medium text-indigo-700">Parcial {p}:</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            maxLength={5}
                            value={taskWeights[`parcial_${p}`] || ''}
                            onChange={e => handleWeightChange(`parcial_${p}`, e.target.value)}
                            disabled={isReadOnly}
                            placeholder="0"
                            className="w-14 h-8 text-center text-sm font-bold border-2 border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                            data-testid={`task-weight-p${p}`}
                          />
                          <span className="text-sm text-indigo-500">%</span>
                        </div>
                      ))}
                    </div>

                    {hasAnyWeight && blocks.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="border-b-2 border-indigo-200 bg-indigo-50">
                              <th className="text-left py-2 px-3 text-sm font-semibold text-indigo-700 min-w-[180px]">Alumno</th>
                              {blocks.map((b, i) => {
                                const key = parcialKeys[i];
                                const weight = parseWeight(taskWeights[key]);
                                return weight > 0 ? (
                                  <th key={i} className="text-center py-2 px-2 text-xs font-semibold text-indigo-700 min-w-[100px]">
                                    <div>{b.label}</div>
                                    <div className="text-indigo-400 font-normal">Peso: {weight}%</div>
                                  </th>
                                ) : null;
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredStudents.map(student => {
                              const rec = taskGrid.records[student.id] || {};
                              return (
                                <tr key={student.id} className="border-b border-slate-200 hover:bg-slate-50">
                                  <td className="py-2 px-3 text-sm font-medium text-slate-900">
                                    {student.name}
                                    {student.matricula && <span className="block text-[10px] text-slate-400 font-normal">Mat: {student.matricula}</span>}
                                  </td>
                                  {blocks.map((b, i) => {
                                    const key = parcialKeys[i];
                                    const weight = parseWeight(taskWeights[key]);
                                    if (weight <= 0) return null;
                                    const vals = b.taskIds.map(id => rec[id]).filter(v => v !== undefined && v !== null && v !== '');
                                    if (vals.length === 0) {
                                      return <td key={i} className="text-center py-2 px-2 text-sm text-slate-400">--</td>;
                                    }
                                    const avg = vals.reduce((a, v) => a + (parseInt(v, 10) || 0), 0) / vals.length;
                                    const grade = (avg / 100) * (weight / 100) * 10;
                                    const color = grade >= (weight / 100 * 8) ? 'text-green-700 bg-green-50' : grade >= (weight / 100 * 6) ? 'text-yellow-700 bg-yellow-50' : 'text-red-700 bg-red-50';
                                    return (
                                      <td key={i} className={`text-center py-2 px-2 text-sm font-bold ${color} rounded`}>
                                        {grade.toFixed(2)}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {!hasAnyWeight && (
                      <p className="text-sm text-slate-500 text-center py-3">Especifica el porcentaje de tareas en cada parcial para ver las calificaciones</p>
                    )}
                  </div>
                );
              })()}

            </Card>
          </TabsContent>

          {/* Participación Cívica Tab */}
          <TabsContent value="civic">
            <Card className="p-4">
              <React.Suspense fallback={<div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div></div>}>
                <CivicParticipationGrid
                  classId={classId}
                  students={students}
                  role="teacher"
                  isTutor={false}
                  orientadorJustifications={orientadorJustifications}
                  onSaveStatus={reportSave}
                  partialDates={partialDates}
                  refreshTick={civicEvalPollTick}
                  suspensionDates={(attendanceGrid.columns || []).filter(c => c.isSuspension).map(c => c.date)}
                  conflictTick={civicConflictCheck}
                  flushRef={civicFlushRef}
                />
              </React.Suspense>
            </Card>
          </TabsContent>

          <TabsContent value="eval">
            <Card className="p-4">
              <React.Suspense fallback={<div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>}>
                <EvalComplementariaGrid
                  classId={classId}
                  students={students}
                  onSaveStatus={reportSave}
                  partialDates={partialDates}
                  refreshTick={civicEvalPollTick}
                  flushRef={evalFlushRef}
                  attendanceByParcial={attendanceByParcial}
                />
              </React.Suspense>
            </Card>
          </TabsContent>

        </Tabs>

        </div>{/* /print-area */}

        {/* Suspension conflict dialog (Tareas) */}
        <Dialog open={!!conflictTask} onOpenChange={(o) => { if (!o) { setConflictTask(null); setConflictReprogram(false); } }}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-amber-700">Coincide con día de suspensión</DialogTitle>
            </DialogHeader>
            {conflictTask && (
              <div className="space-y-4">
                <p className="text-sm text-slate-700">
                  La tarea <strong>{conflictTask.label}</strong>
                  {conflictTask.description ? <> (“{conflictTask.description}”)</> : null} está programada en un día de <strong>suspensión de clases</strong>. Puedes reprogramarla o mantenerla (por ejemplo si la pedirás por correo).
                </p>
                {!conflictReprogram ? (
                  <div className="flex flex-col gap-2">
                    <Button onClick={() => setConflictReprogram(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="conflict-task-reprogram">Reprogramar</Button>
                    <Button variant="outline" onClick={handleKeepTask} data-testid="conflict-task-keep">Mantener y no avisar para esta tarea</Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Nueva fecha</Label>
                    <Input type="date" value={conflictNewDate} onChange={(e) => setConflictNewDate(e.target.value)} data-testid="conflict-task-newdate" />
                    <div className="flex gap-2 pt-1">
                      <Button variant="outline" className="flex-1" onClick={() => setConflictReprogram(false)}>Cancelar</Button>
                      <Button className="flex-1 bg-orange-500 hover:bg-orange-600" onClick={handleReprogramTask} data-testid="conflict-task-confirm">Confirmar</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Separator Dialog */}
        <Dialog open={addSeparatorDialog} onOpenChange={setAddSeparatorDialog}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="w-2 h-6 bg-purple-400 rounded"></span>
                Agregar Separador de Parcial
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddSeparator} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="separator-label">Nombre del Parcial (opcional)</Label>
                <Input
                  id="separator-label"
                  value={separatorLabel}
                  onChange={(e) => setSeparatorLabel(e.target.value)}
                  placeholder="Ej: P1, Parcial 1, etc."
                  data-testid="separator-label-input"
                />
                <p className="text-xs text-slate-500">
                  Este separador ayudará a visualizar el fin de un parcial en la cuadrícula.
                </p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-700">
                  Se agregará en: <strong>{separatorType === 'attendance' ? 'Asistencias' : 'Tareas'}</strong>
                </p>
              </div>
              <Button type="submit" className="w-full bg-purple-500 hover:bg-purple-600">
                Agregar Separador
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Popup para nombre de tarea (mobile) */}
      {taskNamePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setTaskNamePopup(null)}>
          <div className="bg-white rounded-xl shadow-lg p-5 mx-4 max-w-sm" onClick={e => e.stopPropagation()} data-testid="task-name-popup">
            <p className="text-sm font-semibold text-slate-900 mb-3">{taskNamePopup}</p>
            <button onClick={() => setTaskNamePopup(null)} className="w-full py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-colors">Cerrar</button>
          </div>
        </div>
      )}

      {/* Isolated note/edit popover: its own local state keeps keystrokes from
          re-rendering the whole task grid. */}
      <CellNoteDialog
        open={!!attNoteDialog}
        mode="attendance"
        prefix="attendance"
        studentId={attNoteDialog?.studentId}
        colId={attNoteDialog?.colId}
        initialValue={attNoteDialog?.initialValue || ''}
        initialText={attNoteDialog?.initialText || ''}
        isReadOnly={isReadOnly}
        onClose={() => setAttNoteDialog(null)}
        onSave={handleAttNoteSave}
      />
      <TaskNoteDialog
        open={!!noteDialog}
        studentId={noteDialog?.studentId}
        colId={noteDialog?.colId}
        initialValue={noteDialog?.initialValue || ''}
        initialText={noteDialog?.initialText || ''}
        isReadOnly={isReadOnly}
        onClose={() => setNoteDialog(null)}
        onSave={handleNoteSave}
      />
    </Layout>
  );
};

export default ClassView;
